// Helsinki, "Snow over the South Harbour": a December night, light snow falling on the open water of Eteläsatama.
// The viewer stands on the Makasiiniranta quay at the south end of Eteläranta (60.1625 N, 24.9545 E) and looks up
// the harbour to the north-north-east (bearing about 5°). Left to right, as seen from there: a lit café pavilion on
// the quay (the cozy corner, its roof sign the billboard), the Old Market Hall on the west quay (bearing -13°), the
// Cathedral over City Hall (-8°, 890 m, white on its platform above Senate Square), the Market Square's waterfront row
// (City Hall, the Swedish Embassy, the Supreme Court, the Presidential Palace at +10°), the Kolera basin and the
// Market Square's quays, the Enso building's white block on Katajanokka (+15°), the Allas Sea Pool steaming on the
// water (+18°), Uspenski Cathedral on its rock over the Kanavaranta warehouses (+24°, 730 m) and the SkyWheel on the
// Katajanokka quay (+30°). Seen from any quay of the harbour the two cathedrals stand about six times their height
// apart, so both are drawn about three times their true angular size, as on the postcards; the order, the sides and
// what stands in front of what are as seen from the quay.
// Back to front: deep winter sky, stars, the moon -> the far roofs of Kruununhaka -> the Cathedral (floodlit white,
// verdigris domes) -> Uspenski (warm-lit brick, gold onions) -> the waterfront row and the Market Square, the Market
// Hall, the Enso block, the Kanavaranta warehouses -> the SkyWheel -> Allas on the water -> the harbour mirroring it all
// -> the snowy quay with the café pavilion, people and bollards -> snow at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, rect, ditherFill, makeHalo, drawHalo, glyph, FONT3, streak, makePitch, gradeAd } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 160;                                   // the far waterline under the Market Square's quays
const QY = x => Math.round(186 + x * 12 / W);     // the near quay's granite edge (the quay runs off to the left, toward the Market Hall)
const FEET = 205;                                 // the near people's feet (above the bottom 63 rows)
const MOON = { x: 296, y: 30, r: 7 };
// Helsinki Cathedral (Engel, 1852; 62 m to the cross). Real ratios, measured on frontal photos from the harbour:
// height (cross to the colonnade's foot) to width across the side porticoes about 1.0; the drum, dome and lantern
// above the roofs 0.53 of the height; the green dome 0.21 of the width, the drum 0.24 of the height; the colonnade
// 0.23 of it. Here: 104 px high (rows 48..152), 104 wide, the drum group 55 px, dome 22 px wide, drum 25.
const CATH = { cx: 140, tip: 48, base: 152, hw: 52 };
const CD = { domeTop: 59, domeBase: 73, drumTop: 76, drumBase: 103, drumHw: 12, domeHw: 11, towerDx: 29,
  pedApex: 115, cornice: 125, colTop: 129, porticoHw: 19 };
// Uspenski Cathedral (Gornostayev, 1868; 51 m). Real: height to the body's width about 1.33; the central tower
// (drum, tented roof, onion and cross) 0.55 of the height; onion and cross 0.15; the drum 0.37 of the body's width.
// Here: 84 px high (rows 62..146), body 62 wide, the tower 46 px, onion and cross 13, drum 23.
const USP = { cx: 379, tip: 62, base: 146, hw: 31 };
const WHEEL = { cx: 436, cy: 122, r: 27, base: 158 };   // the SkyWheel (40 m, 30 gondolas), on the Katajanokka quay
const ENSO = { x0: 300, x1: 330, top: 128 };            // the Enso building's marble block
const ALLAS = { x0: 296, x1: 368, y: 157 };             // the Allas Sea Pool's decks on the water
const HALL = { x0: 60, x1: 98, peak: 139 };             // the Old Market Hall's south gable
// the quay café near the viewer (the cozy corner): its gable front x0..x1 from the apex to the plinth's foot, the side
// wall running back to x = far (eave and foot rows at that end)
const CAFE = { x0: 0, x1: 78, apex: 58, eave: 96, sign: 141, base: 228, far: 88, farEave: 120, farBase: 205 };
const BB = { x: 14, y: 104, w: 50, h: 20 };             // the lightbox on the café's upper floor: the billboard's face
const STALL = { x0: 100, x1: 138, roof: 159, base: 198 };   // the doughnut and coffee stall on the quay
const LAMPS = [{ x: 230, top: 134, base: 196 }];          // a harbour lamp at the quay's edge
const NEAR_LAMP = { x: 472, top: 108, base: 262 };       // the tall cast-iron lamp near the viewer

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h || !col) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const get = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const fill = (x0, y0, ww, hh, col) => { for (let y = y0; y < y0 + hh; y++) for (let i = x0; i < x0 + ww; i++) put(i, y, col); };
  return { c, x, put, get, fill, done: () => { x.putImageData(img, 0, 0); return c; } };
}
function mixHex(a, b, t) {
  const A = hex(a), B = hex(b), f = k => Math.round(lerp(A[k], B[k], t)).toString(16).padStart(2, '0');
  return `#${f(0)}${f(1)}${f(2)}`;
}
function ramp(pal, v, i, y) {
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f); if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
}
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };

/* ================= Sign font: FONT3 with wide M, W, N, K, V and the Finnish Ä ================= */
// The 3 px K and W read as H at 1x and the N as Π; Ä's dots ride a row over the cap height (drawn from y - 1).
const WIDE = {
  M: ['10001', '11011', '10101', '10001', '10001'], W: ['10001', '10001', '10101', '10101', '01010'],
  V: ['10001', '10001', '01010', '01010', '00100'], N: ['1001', '1101', '1011', '1001', '1001'],
  K: ['1001', '1010', '1100', '1010', '1001'], 'Ä': ['101', '010', '101', '111', '101', '101'],
  'Ö': ['101', '010', '101', '101', '101', '010'],
};
const RISE = { 'Ä': 1, 'Ö': 1 };
const glyphOf = ch => WIDE[ch] ?? FONT3[ch];
const signWidth = s => [...s.toUpperCase()].reduce((w, ch) => w + (glyphOf(ch)?.[0].length ?? 3) + 1, 0) - (s ? 1 : 0);
function sign(c, s, x, y, col) {
  let cx = Math.round(x);
  for (const ch of s.toUpperCase()) { const g = glyphOf(ch); if (g) glyph(c, g, cx, Math.round(y) - (RISE[ch] || 0), col); cx += (g?.[0].length ?? 3) + 1; }
  return cx - x - 1;
}

/* ================= Sky: deep winter blue, the city's glow low over the harbour ================= */
const SKY = ['#03051a', '#050820', '#070b27', '#0a0f2e', '#0d1336', '#11173e', '#151b45', '#1b204c', '#222552',
  '#2b2a58', '#35305c', '#40365e', '#4c3d60'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 166), 1.6) * 0.86;
    v += Math.max(0, 1 - Math.hypot((i - CATH.cx) / 170, (y - 160) / 70)) * 0.12;   // the floodlit Cathedral's glow
    v += Math.max(0, 1 - Math.hypot((i - 400) / 150, (y - 160) / 60)) * 0.1;         // Katajanokka and the wheel
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 64, (y - MOON.y) / 46)) * 0.1;    // moonlit sky round the moon
    return v;
  });
}
function renderMoon() {
  const r = MOON.r, s = r * 2 + 1, p = painter(s, s);
  const maria = [[-2, -2, 1.7], [2, 1, 1.5], [1, -3, 1], [-2, 3, 1], [3, -1, 0.8]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -5 ? '#fffcee' : '#f2eedc';
    if (dd > r - 1.1) col = dx + dy > 0 ? '#c2bea8' : '#e2dcc8';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dd > r - 1.1 ? '#b8b29e' : '#d6d0bc';
    p.put(i, y, col);
  }
  return p.done();
}
const STARS = [];
{
  const r = rng(60);
  while (STARS.length < 190) {
    const x = Math.floor(r() * W), y = 2 + Math.floor(r() * 118);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 16) continue;
    STARS.push({ x, y, k: 10 + Math.floor(r() * 50), ph: r(), b: y < 60 ? 0.45 + r() * 0.5 : 0.22 + r() * 0.3, big: r() < 0.07 });
  }
}

// small snow clouds drifting slowly east, lit by the moon on their tops, no outlines, dithered into the sky (tileable)
const WISPS = [{ seed: 5, y: 4, h: 22, k: 2 }, { seed: 9, y: 28, h: 22, k: 3 }, { seed: 14, y: 52, h: 22, k: 2 }, { seed: 21, y: 78, h: 24, k: 3 }, { seed: 26, y: 102, h: 24, k: 2 }];
function renderWisp(def) {
  const r = rng(def.seed), h = def.h, blobs = [];
  for (let b = 0; b < 3; b++) {                                                         // a few cloudlets, each a cluster of puffs
    const cx = r() * W, len = 40 + r() * 50;
    for (let k = 0; k < 7; k++) { const u = (k / 6 - 0.5); blobs.push({ x: cx + u * len + (r() - 0.5) * 8, y: h * 0.62 - (1 - Math.abs(u) * 2) * (2 + r() * 3), rx: 7 + r() * 9, ry: 2.5 + r() * 2.5 }); }
  }
  const p = painter(W, h);
  const sky = SKY[Math.round(Math.pow(Math.min(1, (def.y + h / 2) / 166), 1.6) * 0.86 * (SKY.length - 1))];
  const tone = [0.15, 0.21, 0.3].map(a => mixHex(sky, '#9aa4d8', a));                   // a shade lighter than the sky round it
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let v = 0;
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; v = Math.max(v, 1 - (dx * dx) / (b.rx * b.rx) - ((y - b.y) ** 2) / (b.ry * b.ry)); }
    if (v <= 0) continue;
    let up = 0;                                                                          // the moonlit top: the edge above is open sky
    for (const b of blobs) { let dx = x - b.x; dx -= Math.round(dx / W) * W; up = Math.max(up, 1 - (dx * dx) / (b.rx * b.rx) - ((y - 2 - b.y) ** 2) / (b.ry * b.ry)); }
    if (v > 0.35) p.put(x, y, up <= 0.1 ? tone[2] : v > 0.7 ? tone[1] : tone[0]);
    else if ((v / 0.35) > bayer(x, y)) p.put(x, y, tone[0]);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ================= Far roofs: Kruununhaka and the city behind the waterfront ================= */
const NIGHT = '#141a3c';
const SNOW = ['#eef2ff', '#ccd4f0', '#a4acd0', '#7c84aa'];
const WARMW = ['#ffe2a4', '#f8c672', '#e8a456'];
function renderFar() {
  const p = painter(W, H), put = p.put, r = rng(11);
  // a band of dark roofs over the waterfront row, a few lit windows, chimneys; it stops where the landmarks stand
  let x = 60;
  while (x < 470) {
    const w = 10 + Math.floor(r() * 16), top = 128 + Math.floor(r() * 9);
    for (let y = top; y < 150; y++) for (let i = x; i < x + w; i++) {
      const roof = y < top + 3;
      put(i, y, roof ? (y === top ? mixHex(SNOW[2], NIGHT, 0.45) : mixHex(SNOW[3], NIGHT, 0.5)) : (i + y) % 7 === 0 ? '#20244a' : '#191d40');
    }
    for (let y = top + 5; y < 148; y += 4) for (let i = x + 2; i < x + w - 2; i += 3) {
      if (hash(i, y, 3) < 0.2) put(i, y, hash(i, y, 4) < 0.5 ? mixHex(WARMW[1], NIGHT, 0.35) : mixHex(WARMW[2], NIGHT, 0.45));
      else if (hash(i, y, 5) < 0.25) FAR_WINS.push({ id: FAR_WINS.length + 800, x: i, y, w: 1, h: 1, n: 5 + Math.floor(hash(i, y, 6) * 8), ph: hash(i, y, 7) });
    }
    if (r() < 0.4) for (let y = top - 4; y < top; y++) put(x + 3, y, y === top - 4 ? mixHex(SNOW[2], NIGHT, 0.4) : '#22264a');
    x += w;
  }
  return p.done();
}

/* ================= Helsinki Cathedral: floodlit white, verdigris domes, gilt crosses ================= */
// Seen from the south, nearly front-on (the rear corner towers show their domes inside the front ones, as from the
// harbour): the south portico of six Corinthian columns under its pediment and three apostles,
// the west and east arms on each side with the end columns of their own porticoes, the low zinc roofs under the
// snow, the four corner towers with their small starred domes, the tall colonnaded drum with its clock, the big
// ribbed dome, the lantern, the gilt ball and cross. It is the brightest thing in the frame.
const WH = { hi: '#fdfcf6', lt: '#eef0f2', md: '#d2d7e2', sh: '#a6aec4', dk: '#737c98', deep: '#4c5474' };
const VERD = { hi: '#78c4aa', lt: '#4c9e88', md: '#357e6c', sh: '#245e54', dk: '#173f3c' };
const GILT = ['#fff2b4', '#f2c65c', '#b8882e'];
const ZINC = ['#7c9a96', '#5e7c78', '#45605e'];
// a dome: rows from its base up, hw(k) half-widths, filled verdigris lit from the front left and from below
function verdDome(put, cx, base, prof, stars = false) {
  const n = prof.length;
  for (let k = 0; k < n; k++) {
    const hw = prof[k], y = base - k;
    for (let i = cx - hw; i <= cx + hw; i++) {
      const u = (i - cx) / Math.max(1, hw), v = 0.64 - u * 0.44 - k / n * 0.34 + (bayer(i, y) - 0.5) * 0.08;
      let col = v > 0.8 ? VERD.hi : v > 0.55 ? VERD.lt : v > 0.3 ? VERD.md : v > 0.08 ? VERD.sh : VERD.dk;
      if (!stars && hw > 5 && (i - cx + 30) % 4 === 0 && Math.abs(u) < 0.8) col = v > 0.55 ? VERD.md : v > 0.3 ? VERD.sh : VERD.dk;   // ribs
      put(i, y, col);
    }
  }
  if (stars) for (const [dx, dk] of [[-2, 2], [1, 3], [3, 1], [-3, 0], [0, 5]]) if (dk < n && Math.abs(dx) <= prof[dk] - 1) put(cx + dx, base - dk, GILT[1]);
}
function crossOn(put, cx, top, h, ball = true) {        // a gilt ball and Latin cross, h rows of shaft over the ball
  for (let y = top; y < top + h; y++) put(cx, y, y < top + 2 ? GILT[0] : GILT[1]);
  put(cx - 1, top + 2, GILT[1]); put(cx + 1, top + 2, GILT[2]);
  if (ball) { put(cx, top + h, GILT[0]); put(cx - 1, top + h, GILT[1]); put(cx + 1, top + h, GILT[2]); put(cx, top + h + 1, GILT[2]); }
}
// white stone shaded across a cylinder of half-width hw (lit from the front left)
const drumCol = (i, cx, hw, y, lift = 0) => {
  const u = (i - cx) / hw, v = 0.78 - u * 0.45 - Math.abs(u) ** 3 * 0.25 + lift + (bayer(i, y) - 0.5) * 0.05;
  return v > 0.86 ? WH.hi : v > 0.66 ? WH.lt : v > 0.46 ? WH.md : v > 0.28 ? WH.sh : WH.dk;
};
function cornerTower(put, cx, dim) {                   // a corner tower: block with a small pediment, round dome, cross
  const m = c => dim ? mixHex(c, '#46507a', dim) : c;
  for (let y = 101; y <= 116; y++) for (let i = cx - 6; i <= cx + 6; i++) {
    const ped = y < 105 && Math.abs(i - cx) > (y - 101) * 2 + 1;
    if (ped) continue;
    let col = i === cx - 6 ? WH.hi : i === cx + 6 ? WH.sh : i > cx + 3 ? WH.md : WH.lt;
    if (y < 105 && Math.abs(i - cx) === (y - 101) * 2 + 1) col = WH.hi;                // the pediment's raking edge
    if (y === 105) col = WH.hi;
    if (y >= 107 && y <= 112 && Math.abs(i - cx) <= 1) col = y === 107 ? WH.sh : '#3c4466'; // its arched window
    put(i, y, m(col));
  }
  for (let i = cx - 4; i <= cx + 4; i++) put(i, 100, m(WH.hi));                        // the dome's ring
  verdDome((i, y, c) => put(i, y, m(c)), cx, 99, [5, 5, 5, 4, 4, 3, 2, 1], true);
  crossOn((i, y, c) => put(i, y, m(c)), cx, 86, 4);
}
function renderCathedral() {
  const p = painter(W, H), put = p.put, { cx } = CATH;
  // ---- the rear corner towers, peeking out behind the front ones ----
  for (const s of [-1, 1]) cornerTower(put, cx + s * (CD.towerDx - 12), 0.2);
  // ---- the central block's attic round the drum's foot ----
  for (let y = 104; y < CD.cornice; y++) for (let i = cx - 24; i <= cx + 24; i++) {
    const v = 0.7 - (i - cx) / 60, roof = y >= 112;
    put(i, y, roof ? (y === 112 ? SNOW[0] : y < 116 ? SNOW[1] : y < 120 ? ZINC[0] : ZINC[1])
      : y === 104 ? WH.hi : y === 105 ? WH.sh : (i - cx + 40) % 8 === 0 ? WH.hi : v > 0.6 ? WH.lt : WH.md);
  }
  for (let i = cx - 24; i <= cx + 24; i += 2) put(i, 103, WH.lt);                      // the balustrade
  // ---- the drum: plain foot, the ring of Corinthian columns with tall windows, the attic, the cornice ----
  const dh = CD.drumHw;
  for (let y = CD.drumTop; y <= CD.drumBase; y++) for (let i = cx - dh; i <= cx + dh; i++) {
    const rel = i - cx + 30, u = Math.abs(i - cx) / dh;
    let col = drumCol(i, cx, dh, y, y > 96 ? 0.04 : 0);
    const colonnade = y >= 80 && y <= 96, inWin = rel % 4 >= 1 && rel % 4 <= 2 && u < 0.86;
    if (colonnade && rel % 4 === 0 && u < 0.95) col = drumCol(i, cx, dh, y, 0.2);                       // column shafts, lit
    else if (colonnade && inWin && y >= 85 && y <= 94) col = y === 85 ? drumCol(i, cx, dh, y, -0.1) : u > 0.6 ? '#6a7498' : '#4a5478';   // windows, round-topped
    if (colonnade && rel % 4 === 3 && u < 0.95) col = drumCol(i, cx, dh, y, -0.06);                      // the shade beside each column
    if (y === 80 || y === 81) col = drumCol(i, cx, dh, y, 0.14);                                          // the capitals' band
    if (y === CD.drumTop || y === 97) col = WH.hi;
    if (y === 98) col = drumCol(i, cx, dh, y, -0.18);                                                     // the shadow under the colonnade's base
    put(i, y, col);
  }
  for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) put(cx + i, 83 + j, i || j ? GILT[1] : '#2a2e48');   // the clock's dial
  for (let y = 77; y <= 78; y++) for (let i = cx - 9; i <= cx + 9; i += 3) put(i, y, WH.sh);                         // attic windows
  for (let i = cx - dh - 1; i <= cx + dh + 1; i++) { put(i, 74, WH.hi); put(i, 75, WH.md); }                        // the dome's cornice
  // ---- the dome: stilted, ribbed; the lantern, ball and cross ----
  const prof = [];
  for (let k = 0; k <= CD.domeBase - CD.domeTop; k++) prof.push(k <= 2 ? CD.domeHw : Math.max(2, Math.round(CD.domeHw * Math.sqrt(Math.max(0, 1 - ((k - 2) / 13) ** 2)))));
  verdDome(put, cx, CD.domeBase, prof);
  for (let y = 56; y <= 58; y++) for (let i = cx - 2; i <= cx + 2; i++) put(i, y, y === 56 ? VERD.lt : i < cx ? VERD.md : i === cx ? VERD.lt : VERD.sh);
  crossOn(put, cx, 48, 6);
  // ---- the front corner towers ----
  for (const s of [-1, 1]) cornerTower(put, cx + s * CD.towerDx, 0);
  // ---- the west and east arms: walls with pilasters and windows, the low roofs under snow, their portico ends ----
  for (const s of [-1, 1]) {
    const a = s < 0 ? cx - CATH.hw : cx + CD.porticoHw + 1, b = s < 0 ? cx - CD.porticoHw - 1 : cx + CATH.hw;
    for (let y = 118; y < CD.cornice; y++) for (let i = a; i <= b; i++) {                // the roof's slope, snow on it
      const f = (y - 118) / 7;
      put(i, y, y === 118 ? SNOW[0] : y === 119 ? SNOW[1] : f < 0.6 ? ZINC[0] : f < 0.86 ? ZINC[1] : ZINC[2]);
    }
    for (let y = CD.cornice; y <= CATH.base; y++) for (let i = a; i <= b; i++) {
      const outer = s < 0 ? i - a : b - i;                                                 // 0 at the arm's far end
      let col = (y - 125) < 3 ? (y === 125 ? WH.hi : y === 126 ? WH.md : WH.lt) : outer < 9 && s > 0 ? WH.md : WH.lt;
      if (y >= 128 && outer <= 1) col = outer === 0 ? WH.hi : WH.lt;                       // the end column of the side portico
      else if (y >= 128 && outer >= 2 && outer <= 4) col = outer === 2 ? WH.dk : WH.deep;  // the portico's shade behind it
      else if (y >= 128 && (outer - 5) % 7 === 0) col = WH.hi;                              // pilasters
      else if (y >= 132 && y <= 144 && (outer - 5) % 7 >= 2 && (outer - 5) % 7 <= 4) col = y === 132 ? WH.sh : '#3a4264';   // windows
      put(i, y, col);
    }
    put(s < 0 ? a : b, 121, WH.lt); put(s < 0 ? a : b, 122, WH.md); put(s < 0 ? a : b, 123, WH.md); put(s < 0 ? a : b, 124, WH.sh);   // its apostle
  }
  // ---- the south portico: the back wall in shade, six columns, the entablature, the pediment and its apostles ----
  const ph = CD.porticoHw;
  for (let y = CD.colTop; y <= CATH.base; y++) for (let i = cx - ph; i <= cx + ph - 1; i++) {
    const door = Math.abs(i - cx + 0.5) <= 3 && y >= 138;
    put(i, y, door ? (y < 140 ? WH.sh : '#e8b46a') : y < CD.colTop + 3 ? WH.deep : y > 136 && y < 139 ? WH.sh : WH.dk);
  }
  for (let k = 0; k < 6; k++) {
    const x0 = cx - ph + k * 7;
    for (let y = CD.colTop; y <= CATH.base; y++) { put(x0, y, WH.hi); put(x0 + 1, y, WH.lt); put(x0 + 2, y, WH.md); }
    for (let i = x0 - 1; i <= x0 + 3; i++) { put(i, CD.colTop, WH.hi); put(i, CD.colTop + 1, i > x0 + 1 ? WH.md : WH.lt); }   // capitals
  }
  for (let i = cx - ph - 2; i <= cx + ph + 1; i++) {
    put(i, CD.cornice, WH.hi); put(i, CD.cornice + 1, WH.sh); put(i, CD.cornice + 2, WH.lt); put(i, CD.cornice + 3, WH.md);
  }
  for (let y = CD.pedApex; y < CD.cornice; y++) {
    const hw = Math.round((y - CD.pedApex) * (ph + 2) / (CD.cornice - CD.pedApex));
    for (let i = cx - hw; i <= cx + hw - 1; i++) {
      const d = hw - Math.abs(i - cx + 0.5);
      put(i, y, d < 1 ? WH.hi : d < 2 ? WH.sh : y === CD.cornice - 1 ? WH.lt : WH.md);   // raking cornice, its shadow, the tympanum
    }
  }
  const apostle = (x, y) => { put(x, y, WH.lt); put(x, y + 1, WH.md); put(x - 1, y + 2, WH.md); put(x, y + 2, WH.lt); put(x, y + 3, WH.sh); };
  apostle(cx, CD.pedApex - 4); apostle(cx - ph - 1, CD.cornice - 4); apostle(cx + ph, CD.cornice - 4);
  return p.done();
}

/* ================= Uspenski Cathedral: warm-lit red brick on its rock, green roofs, gold onions ================= */
// Seen from the south-west across the harbour: the square brick body with its round-arched gables and pale stone
// bands, the octagonal drum with tall arched windows, the bell-shaped green tented roof, the gilt onion and the
// Orthodox cross; four turrets round the drum and at the corners, each a green tent under a small gold onion. The rock
// it stands on shows over the Kanavaranta warehouses at its foot, bare trees and snow on it.
const BRICK = { hi: '#f4a47c', lt: '#da7450', md: '#b4543c', sh: '#883c30', dk: '#5c2a28' };
const UGRN = { hi: '#62a892', lt: '#3e826e', md: '#2c6258', sh: '#1f4844', dk: '#14302f' };
const UGOLD = ['#fff6c0', '#f8ce60', '#c88e2e', '#80561e'];
function onion(put, cx, top, prof) {                   // a gilt onion: rows from its point down
  prof.forEach((hw, k) => { for (let i = cx - hw; i <= cx + hw; i++) { const u = (i - cx) / Math.max(1, hw); put(i, top + k, u < -0.3 ? UGOLD[0] : u < 0.35 ? UGOLD[1] : UGOLD[2]); } });
}
function tent(put, cx, top, prof, dim = 0) {           // a green tented roof: rows from the top down, lit from the left
  prof.forEach((hw, k) => {
    for (let i = cx - hw; i <= cx + hw; i++) {
      const u = (i - cx) / Math.max(1, hw), v = 0.55 - u * 0.4 + k / prof.length * 0.2 - dim + (bayer(i, top + k) - 0.5) * 0.18;
      let col = v > 0.72 ? UGRN.hi : v > 0.5 ? UGRN.lt : v > 0.3 ? UGRN.md : v > 0.12 ? UGRN.sh : UGRN.dk;
      if (hw > 4 && (i - cx + 40) % 3 === 0 && Math.abs(u) < 0.8) col = v > 0.45 ? UGRN.md : UGRN.dk;   // standing seams
      put(i, top + k, col);
    }
  });
}
function brickAt(i, y, light) {                         // floodlit brick: brighter low down, courses in the shade
  const v = light + (USP.base - y) * -0.004 + (bayer(i, y) - 0.5) * 0.16 - ((y & 1) && hash(i >> 1, y, 5) < 0.2 ? 0.08 : 0);
  return v > 0.8 ? BRICK.hi : v > 0.58 ? BRICK.lt : v > 0.36 ? BRICK.md : v > 0.16 ? BRICK.sh : BRICK.dk;
}
function turret(put, cx, top, shaft, dim = 0) {         // a turret: cross, onion, green tent, brick shaft
  put(cx, top, UGOLD[1]); put(cx, top + 1, UGOLD[1]); put(cx - 1, top + 1, UGOLD[2]); put(cx + 1, top + 1, UGOLD[2]);
  onion(put, cx, top + 2, [0, 1, 2, 2, 1]);
  tent(put, cx, top + 7, [1, 1, 2, 2, 3, 3, 3], dim);
  for (let y = top + 14; y < top + 14 + shaft; y++) for (let i = cx - 3; i <= cx + 3; i++) {
    const arch = Math.abs(i - cx) <= 0 && y > top + 16 && y < top + 12 + shaft;
    put(i, y, y === top + 14 ? '#e8d2b8' : arch ? '#3a1e22' : brickAt(i, y, 0.62 - (i - cx) * 0.08 - dim));
  }
}
function renderUspenski() {
  const p = painter(W, H), put = p.put, { cx, base } = USP;
  // the rock over the warehouses: dark granite, snow lying on its ledges, bare trees on its slope
  for (let x = cx - 46; x <= cx + 48; x++) {
    const top = base - 3 + Math.round(Math.abs(x - cx) / 7 + 2 * Math.sin(x * 0.7));
    for (let y = top; y < 152; y++) put(x, y, y === top || hash(x, y, 9) < 0.25 ? SNOW[2] : (x + y) % 4 === 0 ? '#2a2638' : '#201e30');
  }
  for (const tx of [cx - 40, cx - 34, cx + 36, cx + 43]) for (let b = 0; b < 6; b++) {
    const a = -Math.PI / 2 + (hash(tx, b, 2) - 0.5) * 2, len = 4 + hash(tx, b, 3) * 6;
    for (let s = 0; s < len; s++) put(tx + Math.cos(a) * s, 148 + Math.sin(a) * s, '#2a2434');
  }
  // the body: the south front of three bays under round-arched gables (zakomaras) trimmed in pale stone, pilaster
  // strips with white capitals, arched windows in white surrounds; the green roof behind the gables
  const TRIM = '#f0dcc0', TRIM2 = '#c8b090';
  for (let y = 106; y <= 117; y++) for (let i = cx - 24; i <= cx + 30; i++) put(i, y, y < 109 ? UGRN.lt : y < 113 ? UGRN.md : UGRN.sh);
  for (let y = 109; y <= base; y++) for (let i = cx - 24; i <= cx + USP.hw; i++) {
    const bay = clamp(Math.floor((i - (cx - 24)) / 18), 0, 2), bx = cx - 24 + bay * 18 + 9, dxb = i - bx;
    const archTop = 108 + Math.round(9 - Math.sqrt(Math.max(0, 81 - dxb * dxb)));          // the gable's round top
    if (y < archTop) continue;
    let col = brickAt(i, y, 0.66 - (i - cx) * 0.004);
    if (y <= archTop + 1) col = y === archTop ? TRIM : TRIM2;                              // pale stone on the arch
    else if (y > archTop + 1 && y <= archTop + 3 && Math.abs(dxb) < 8) col = brickAt(i, y, 0.4);   // the arch's shadowed soffit
    if (y === 119 || y === 120) col = y === 119 ? TRIM : TRIM2;                             // the cornice under the gables
    if (y === 133) col = TRIM2;                                                             // the string course
    if (Math.abs(dxb) === 9) col = y < 121 ? col : y === 121 ? TRIM : BRICK.sh;            // pilaster strips, white capitals
    const rose = Math.hypot(dxb, y - 114) < 2.3 && y > archTop + 2;
    if (rose) col = Math.hypot(dxb, y - 114) < 0.6 ? '#5a3428' : TRIM;                     // the gable's round window
    for (const [wx, t0, t1] of [[-4, 124, 131], [0, 123, 131], [4, 124, 131], [-3, 136, 143], [3, 136, 143]]) {
      if (dxb === wx && y >= t0 && y <= t1) col = y === t0 ? TRIM : y === t0 + 1 ? '#5a3030' : '#2e1820';   // arched windows
      if (Math.abs(dxb - wx) === 1 && y >= t0 + 1 && y <= t1) col = TRIM2;                  // their white surrounds
    }
    put(i, y, col);
  }
  // the bell tower at the west end: a brick shaft, the belfry's arches, a small green tent, a gold onion and cross
  const bt = cx - 27;
  for (let y = 92; y <= base; y++) for (let i = bt - 4; i <= bt + 4; i++) {
    let col = brickAt(i, y, 0.5 - (i - bt) * 0.05);
    if (i === bt - 4) col = BRICK.sh;
    if (y === 92 || y === 106 || y === 119) col = TRIM;
    if (y >= 95 && y <= 103 && (i === bt - 2 || i === bt + 2)) col = y === 95 ? TRIM : '#2a1820';   // the belfry's openings
    if (y >= 124 && y <= 131 && i === bt) col = y === 124 ? TRIM : '#2e1820';
    if (y > 133 && y < 136 && Math.abs(i - bt) <= 1) col = TRIM2;
    put(i, y, col);
  }
  tent(put, bt, 83, [1, 1, 2, 2, 3, 3, 4, 4, 5]);
  onion(put, bt, 78, [0, 1, 2, 2, 1]);
  put(bt, 75, UGOLD[1]); put(bt, 76, UGOLD[1]); put(bt, 77, UGOLD[1]); put(bt - 1, 76, UGOLD[2]); put(bt + 1, 76, UGOLD[2]);
  // the corner turrets (outer, lower) and the rear pair, then the octagonal drum and the main tower
  turret(put, cx + 28, 97, 11, 0.18);
  turret(put, cx - 14, 86, 14, 0.12); turret(put, cx + 15, 87, 13, 0.22);
  for (let y = 93; y <= 108; y++) for (let i = cx - 11; i <= cx + 11; i++) {
    const face = i < cx - 5 ? 0.5 : i <= cx + 5 ? 0.72 : 0.34, f = i - cx;
    let col = brickAt(i, y, face);
    if (f === -5 || f === 6) col = BRICK.sh;                                               // the octagon's edges
    const win = y >= 97 && y <= 105 && (f === -8 || f === -2 || f === 1 || f === 9) ;
    if (win) col = y === 97 ? '#ecd8bc' : '#2e1820';
    if (y === 93) col = (i & 1) ? '#ecd8bc' : BRICK.lt;                                  // the arcaded cornice
    if (y === 94) col = '#e4ccae';
    put(i, y, col);
  }
  tent(put, cx, 80, [3, 4, 5, 5, 6, 7, 8, 8, 9, 10, 10, 11, 11]);
  for (let y = 76; y <= 79; y++) for (let i = cx - 2; i <= cx + 2; i++) put(i, y, i === cx - 2 ? UGRN.hi : (i - cx) & 1 ? UGRN.dk : UGRN.md);   // the neck
  onion(put, cx, 67, [0, 1, 2, 3, 3, 3, 3, 2, 1]);
  for (let y = 62; y <= 66; y++) put(cx, y, UGOLD[1]);                                   // the cross: three bars
  put(cx - 1, 63, UGOLD[1]); put(cx + 1, 63, UGOLD[1]); put(cx - 1, 65, UGOLD[2]); put(cx + 1, 64, UGOLD[2]);
  // the Kanavaranta warehouses at the waterline: two long red-brick blocks, stepped gables, lit arched windows
  for (const [x0, x1, top] of [[330, 384, 146], [386, 428, 145]]) {
    for (let y = top - 3; y < top; y++) for (let i = x0 + (top - y); i <= x1 - (top - y); i++) put(i, y, y === top - 3 ? SNOW[1] : SNOW[2]);   // snowy roof
    for (let y = top; y < WL; y++) for (let i = x0; i <= x1; i++) {
      let col = brickAt(i, y + 30, 0.46 - (i - x0) * 0.002);
      if (y === top) col = '#d8c0a8';
      const wx = (i - x0) % 6, row = y - top;
      if ((row >= 3 && row <= 5 || row >= 8 && row <= 10) && wx >= 2 && wx <= 3) {
        col = hash(i, y, 7) < 0.55 ? (row < 6 ? WARMW[1] : WARMW[0]) : '#2c1a20';
        if ((row === 3 || row === 8) && wx === 2 && hash(i, y, 7) >= 0.55 && hash(i, y, 8) < 0.5) WINS.push({ id: WINS.length + 400, x: i, y, w: 2, h: 3, n: 5 + Math.floor(hash(i, y, 9) * 7), ph: hash(i, y, 10) });
      }
      put(i, y, col);
    }
  }
  return p.done();
}

/* ================= The waterfront: Eteläranta, the Old Market Hall, the Market Square's row, Katajanokka ================= */
// Engel's pastel row along the Market Square, lit by the square's lamps: City Hall (pale blue, a columned centre), the
// Swedish Embassy (salmon), an ochre house, the Supreme Court (cream) and the Presidential Palace (pale yellow, its
// centre a step higher). The Old Market Hall stands on the west quay in red brick and pale stone; the Enso building's
// white block on Katajanokka; Katajanokka's low quay buildings by the SkyWheel.
const ROW = [
  { x0: 96, x1: 106, top: 147, f: ['#c8b89a', '#a89878', '#7a6c58'] },                        // the Esplanadi corner
  { x0: 106, x1: 176, top: 141, f: ['#9cb2cc', '#8098b8', '#5e7294'], cityHall: true },      // City Hall
  { x0: 176, x1: 198, top: 141, f: ['#eeaa92', '#d08870', '#a0624e'], roof: 3 },             // the Swedish Embassy
  { x0: 198, x1: 220, top: 136, f: ['#e8b876', '#c89656', '#98703e'], roof: 4 },
  { x0: 220, x1: 250, top: 137, f: ['#f0e2bc', '#d4c49c', '#a49676'], roof: 4 },             // the Supreme Court
  { x0: 250, x1: 298, top: 136, f: ['#f2e2b0', '#d6c490', '#a6966a'], palace: true, roof: 4 },        // the Presidential Palace
];
const SQ = 156;                                         // the Market Square's ground in front of the row
function renderWaterfront() {
  const p = painter(W, H), put = p.put;
  // Eteläranta's tall fronts at the left edge, over the pavilion
  for (const [x0, x1, top, c] of [[0, 26, 112, '#c8a888'], [26, 46, 118, '#a8b0b8'], [46, 62, 124, '#d8c49c']]) {
    for (let y = top; y < WL; y++) for (let i = x0; i < x1; i++) {
      const col = mixHex(c, NIGHT, 0.42 + (i - x0) / (x1 - x0) * 0.12);
      const wn = (y - top) % 6 >= 2 && (y - top) % 6 <= 3 && (i - x0) % 4 === 1;
      put(i, y, y < top + 2 ? SNOW[2] : wn ? (hash(i, y, 2) < 0.4 ? WARMW[1] : '#262a48') : col);
      if (wn && (y - top) % 6 === 2 && hash(i, y, 2) >= 0.4 && hash(i, y, 3) < 0.5) WINS.push({ id: WINS.length + 600, x: i, y, w: 1, h: 2, n: 5 + Math.floor(hash(i, y, 4) * 8), ph: hash(i, y, 5) });
    }
  }
  // the Old Market Hall: the long east side receding to the left, the south gable with its stepped centre
  const H0 = HALL;
  for (let y = 146; y < WL; y++) for (let i = 50; i < H0.x0; i++) put(i, y, y < 148 ? SNOW[2] : (i + y) % 5 === 0 ? '#7a3c30' : (y > 150 && y < 156 && i % 3 === 0) ? WARMW[1] : '#8a4636');
  for (let y = H0.peak; y < WL; y++) for (let i = H0.x0; i <= H0.x1; i++) {
    const c = (H0.x0 + H0.x1) / 2, d = Math.abs(i - c);
    const top = d <= 4 ? H0.peak : d <= 9 ? H0.peak + 4 : d <= 13 ? 146 : 147 + Math.round((d - 13) * 0.2);
    if (y < top) continue;
    let col = (y - H0.peak) % 5 === 2 ? '#e8d4b4' : bayer(i, y) < 0.4 ? '#b8644a' : '#9a4e3a';
    if (y === top) col = SNOW[1];
    if (y >= 150 && y <= 155 && d > 1 && (Math.round(i) % 4 === 1 || Math.round(i) % 4 === 2)) col = y === 150 ? '#e8d4b4' : WARMW[1];   // lit arched windows
    if (d <= 1 && y >= 151) col = y === 151 ? '#e8d4b4' : WARMW[0];                       // its door
    put(i, y, col);
  }
  // the Market Square's row: low tin roofs under snow with chimneys and dormers, then the fronts
  for (const B of ROW) if (B.roof) for (let k = 1; k <= B.roof; k++) for (let i = B.x0 + k; i < B.x1 - k; i++)
    put(i, B.top - k, k === B.roof ? SNOW[0] : (i % 7 === 3 && k < B.roof - 1) ? '#3a3e5a' : k === 1 ? SNOW[2] : SNOW[1]);
  for (const B of ROW) if (B.roof) for (const cxh of [B.x0 + 4, B.x1 - 6]) for (let y = B.top - B.roof - 3; y < B.top - 1; y++) put(cxh, y, y === B.top - B.roof - 3 ? SNOW[0] : '#5a4a50');
  for (const B of ROW) {
    for (let y = B.top; y < SQ; y++) for (let i = B.x0; i < B.x1; i++) {
      const v = (y - B.top) / (SQ - B.top) * 0.55 + 0.3 + (bayer(i, y) - 0.5) * 0.16 - (i === B.x1 - 1 ? 0.3 : 0);
      let col = v > 0.66 ? B.f[0] : v > 0.4 ? B.f[1] : B.f[2];
      if (y === B.top) col = SNOW[1];
      if (y === B.top + 1) col = '#eef0f0';
      const fl = (y - B.top - 3) % 4;
      if (y > B.top + 2 && y < SQ - 1 && fl >= 1 && fl <= 2 && (i - B.x0) % 3 === 1 && i < B.x1 - 1) {
        const lit = hash(i, y, B.x0) < (y > SQ - 5 ? 0.7 : B.cityHall ? 0.14 : 0.3);
        if (!lit && fl === 1 && y < SQ - 5 && hash(i, y, 77) < 0.4) WINS.push({ id: WINS.length, x: i, y, w: 1, h: 2, n: 6 + Math.floor(hash(i, y, 78) * 8), ph: hash(i, y, 79) });
        col = fl === 1 ? (lit ? WARMW[0] : '#3a3c5a') : (lit ? WARMW[1] : '#2a2c48');
      }
      put(i, y, col);
    }
    if (B.cityHall) {                                    // City Hall's centre: four columns and a low pediment
      const c = 140;
      for (let y = 136; y <= B.top; y++) { const hw = Math.round((y - 136) * 2.4); for (let i = c - hw; i <= c + hw; i++) put(i, y, Math.abs(i - c) >= hw - 1 ? '#eef0f0' : B.f[1]); }
      for (let y = B.top + 2; y < SQ - 1; y++) for (const k of [-6, -2, 2, 6]) { put(c + k, y, '#f2f2ee'); put(c + k + 1, y, '#c8ccd4'); }
    }
    if (B.palace) {                                      // the palace's raised centre and its pediment
      const c = 274;
      for (let y = 131; y <= B.top; y++) { const hw = 9 + Math.round((y - 131) * 0.5); for (let i = c - hw; i <= c + hw; i++) put(i, y, y === 131 ? SNOW[1] : Math.abs(i - c) >= hw ? '#f8eccc' : B.f[0]); }
      for (let y = 139; y < SQ - 1; y++) for (const k of [-6, -3, 3, 6]) put(c + k, y, '#fff6dc');
    }
  }
  // the Market Square: snow on the setts, covered stalls, the Empress Stone, the quay's edge
  for (let y = SQ; y < WL; y++) for (let i = 96; i < 300; i++) put(i, y, y === WL - 1 ? '#8a8aa0' : bayer(i, y) < 0.6 ? SNOW[1] : SNOW[2]);
  for (const sx of [184, 192, 228, 236, 244]) for (let y = SQ - 2; y < WL - 1; y++) for (let i = sx; i < sx + 6; i++)
    put(i, y, y === SQ - 2 ? SNOW[0] : y === SQ - 1 ? '#d8743a' : '#a8582c');           // stalls under their orange covers
  for (let y = 145; y < WL - 1; y++) put(207, y, y < 147 ? '#e8d8a8' : y > 156 ? '#a09a90' : '#d8d0c4');   // the Empress Stone's obelisk
  put(207, 144, GILT[1]);
  // the Enso block on Katajanokka: white marble in shadow, dark window bands, the lobby lit
  for (let y = ENSO.top; y < WL; y++) for (let i = ENSO.x0; i <= ENSO.x1; i++) {
    let col = (y - ENSO.top) % 3 === 2 ? '#3c4260' : i > ENSO.x1 - 6 ? '#8a90aa' : '#a8aec4';
    if ((y - ENSO.top) % 3 === 2 && hash(i, y, 13) < 0.1) col = WARMW[2];
    if (y >= WL - 4) col = (i % 3) ? '#f0d49a' : '#6a6a80';
    if (y === ENSO.top) col = SNOW[1];
    put(i, y, col);
  }
  // Katajanokka's low quay buildings by the wheel
  for (let y = 146; y < WL; y++) for (let i = 458; i < W; i++) put(i, y, y < 148 ? SNOW[2] : (y - 146) % 4 === 2 && i % 3 === 0 ? (hash(i, y, 6) < 0.5 ? WARMW[1] : '#2a2c48') : '#4a4c6a');
  for (let y = WL - 1; y < WL; y++) for (let i = 300; i < W; i++) put(i, y, '#7a7a92');
  return p.done();
}

/* ================= Allas Sea Pool: decks on the water, the warm pools steaming, swimmers ================= */
// Drawn about 1.5x its size, like the rest of the far shore: the long wooden decks floating at the foot of Katajanokka,
// the big seawater pool lit turquoise, the warm pool and the children's pool, the pavilion with its saunas and café.
const POOLS = [{ x0: 299, x1: 323, col: '#3cc6c4', warm: false }, { x0: 327, x1: 341, col: '#62dccc', warm: true }, { x0: 344, x1: 350, col: '#62dccc', warm: true }];
const SWIMMERS = [{ p: 0, x0: 301, x1: 321, k: 3, ph: 0.1 }, { p: 0, x0: 301, x1: 321, k: 2, ph: 0.6 }, { p: 1, x0: 329, x1: 339, k: 1, ph: 0.3 },
  { p: 1, x0: 329, x1: 339, k: 2, ph: 0.8 }, { p: 2, x0: 345, x1: 349, k: 1, ph: 0.5 }];
function renderAllas() {
  const p = painter(W, H), put = p.put, { x0, x1, y } = ALLAS;
  for (let yy = y - 1; yy <= y + 3; yy++) for (let i = x0; i <= x1; i++) put(i, yy, yy === y - 1 ? SNOW[1] : yy === y + 3 ? '#2a1e22' : (i % 5 === 0 ? '#6a5040' : '#8a6a52'));   // the decks
  for (const P of POOLS) for (let i = P.x0; i <= P.x1; i++) for (let yy = y; yy <= y + 1; yy++) put(i, yy, yy === y ? mixHex(P.col, '#ffffff', 0.25) : P.col);
  for (let yy = 147; yy < y - 1; yy++) for (let i = 352; i <= x1; i++)                    // the pavilion: dark wood, warm windows, snow on it
    put(i, yy, yy === 147 ? SNOW[0] : yy === 148 ? SNOW[1] : (yy > 149 && yy < 154 && (i % 4 === 1 || i % 4 === 2)) ? (yy < 151 ? WARMW[0] : WARMW[1]) : '#4a3a34');
  for (let i = x0; i <= x1; i += 3) put(i, y - 2, '#c8c8d8');                              // railing posts
  for (let i = x0; i <= 350; i++) if (i % 3 !== 1) put(i, y - 3, '#8a8aa0');
  return p.done();
}
function drawAllas(t, crowd) {
  for (const P of POOLS) {                                                                  // the water's light breathes
    ctx.globalAlpha = 0.25 + 0.15 * wave(t, 12, P.x0 * 0.01);
    R(P.x0, ALLAS.y, P.x1 - P.x0 + 1, 1, '#c8fff4');
  }
  ctx.globalAlpha = 1;
  if (crowd <= 0) return;
  SWIMMERS.forEach((sw, i) => {                                                             // heads in the water, swimming lengths
    const u = 0.5 + 0.5 * wave(t, sw.k, sw.ph), x = Math.round(lerp(sw.x0, sw.x1, u));
    R(x, ALLAS.y, 1, 1, i % 2 ? '#3a2a24' : '#e8c8a8'); R(x + (wave(t, sw.k, sw.ph + 0.25) > 0 ? 1 : -1), ALLAS.y + 1, 1, 1, '#a8f0e8');
  });
  // a bather in a robe walks the deck to the warm pool and back
  const u = 0.5 + 0.5 * wave(t, 4, 0.15), bx = Math.round(lerp(326, 352, u));
  R(bx, ALLAS.y - 4, 1, 3, '#e8e4dc'); R(bx, ALLAS.y - 5, 1, 1, '#d8a888');
}
function drawSteam(t) {                                         // steam rising off the warm pools in the cold, drifting east
  for (const [sx, k] of [[330, 0], [336, 1], [340, 2], [347, 3]]) for (let j = 0; j < 9; j++) {
    const { u } = cycle(t, 20 + k * 4, k * 0.29 + j / 9), r = Math.min(6, 1 + Math.floor(u * 7));
    halo(HL.steam[r], sx + u * 16 + Math.sin(TAU * (u + k * 0.3)) * 2, ALLAS.y - 1 - u * 30, 0.7 * (1 - u) * smooth(0, 0.08, u));
  }
}

/* ================= The SkyWheel: its A-frame and ticket house (the rim, spokes and gondolas turn per frame) ================= */
function renderWheelBase() {
  const p = painter(W, H), put = p.put, { cx, cy, base } = WHEEL;
  for (const s of [-1, 1]) for (let y = cy; y <= base; y++) {
    const x = cx + s * (y - cy) * 0.36;
    put(x, y, '#d8dce8'); put(x + s, y, '#9aa0b8');
  }
  for (let y = 151; y < base; y++) for (let i = cx - 12; i <= cx + 12; i++) put(i, y, y === 151 ? SNOW[1] : (i % 4 === 0 && y > 152) ? WARMW[1] : '#3a3c56');
  return p.done();
}

/* ================= The near quay: granite slabs under fresh snow, the coping, bollards ================= */
const BOLLARDS = [150, 236, 300, 344, 396, 452];
const warmAt = (x, y) => Math.max(0, 1 - Math.hypot((x - 40) / 84, (y - 236) / 30)) +
  Math.max(0, 1 - Math.hypot((x - 120) / 36, (y - 203) / 9)) * 0.8 +
  Math.max(0, 1 - Math.hypot((x - NEAR_LAMP.x + 14) / 60, (y - 262) / 18)) * 0.8 +
  LAMPS.reduce((m, L) => Math.max(m, Math.max(0, 1 - Math.hypot((x - L.x) / 40, (y - L.base - 6) / 14)) * 0.7), 0);
function vnoise(x, y, sc, seed) {                                // smooth value noise for the snow's drifts
  const fx = x / sc, fy = y / sc, ix = Math.floor(fx), iy = Math.floor(fy), u = fx - ix, v = fy - iy;
  const s = q => q * q * (3 - 2 * q), a = hash(ix, iy, seed), b = hash(ix + 1, iy, seed), c = hash(ix, iy + 1, seed), d = hash(ix + 1, iy + 1, seed);
  return lerp(lerp(a, b, s(u)), lerp(c, d, s(u)), s(v));
}
// night snow in the moonlight, and where the café, the heater and the lamps warm it
const COOLSNOW = ['#262c50', '#30385e', '#3c456c', '#4a547c', '#5a658e', '#6c77a2', '#8088b6'];
const WARMSNOW = ['#4a3a4c', '#6a5058', '#8c6a68', '#b48a7a', '#d8aa8a', '#f2c8a0', '#ffe2be'];
const PATHS = [[[38, 236], [92, 210], [150, 208], [480, 209]], [[266, 201], [292, 270]], [[204, 195], [228, 270]]];
function pathDist(x, y) {
  let m = 1e9;
  for (const P of PATHS) for (let k = 1; k < P.length; k++) {
    const [ax, ay] = P[k - 1], [bx, by] = P[k], dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    const u = clamp(((x - ax) * dx + (y - ay) * dy) / l2, 0, 1);
    m = Math.min(m, Math.hypot(x - ax - u * dx, y - ay - u * dy));
  }
  return m;
}
function renderQuay() {
  const p = painter(W, H), put = p.put;
  for (let x = 0; x < W; x++) {
    const top = QY(x);
    for (let y = top; y < H; y++) {
      if (y <= top + 1) { put(x, y, y === top ? '#a8aec4' : '#3a3e58'); continue; }      // the granite coping
      const z = y - 164, sh = Math.max(2, Math.round(z / 9)), sw = Math.max(6, Math.round(6 + z / 2.2));
      const row = Math.floor(z / sh), off = Math.floor(hash(row, 3, 31) * sw), joint = (x + off) % sw === 0 || z % sh === 0;
      const tw = 3 + z * 0.12, trod = Math.max(0, 1 - pathDist(x, y) / tw);
      const n = vnoise(x, y * 2.2, 9 + z * 0.4, 5) - 0.5;
      let v = 0.66 - (y - top) / (H - top) * 0.36 + n * 0.22 - trod * 0.3 - (trod > 0.3 && joint ? 0.12 : 0);
      if (y === top + 2) v += 0.14;                                                       // a drift along the coping
      if ((y === 209 || y === 212) && x > 96 && hash(x >> 1, y, 9) < 0.8) v -= 0.16;       // the sled's runner tracks
      const w = warmAt(x, y);
      const warm = clamp(w * 1.6, 0, 1) > bayer(x + 1, y + 2);                          // the lamps' light dithers into the moonlit snow
      put(x, y, warm ? ramp(WARMSNOW, clamp(v * 0.75 + w * 0.6 - 0.05, 0, 1), x, y) : ramp(COOLSNOW, v, x, y));
    }
  }
  // footprints along the trodden paths: pairs of prints in the snow
  for (const P of PATHS) for (let k = 1; k < P.length; k++) {
    const [ax, ay] = P[k - 1], [bx2, by] = P[k], len = Math.hypot(bx2 - ax, by - ay);
    for (let q = 3; q < len; q += 5 + Math.floor(hash(k, q, 8) * 3)) {
      const u = q / len, x = ax + (bx2 - ax) * u, y = ay + (by - ay) * u, off = 1 + (y - 186) * 0.04;
      if (y < QY(x) + 3 || (x < CAFE.far + 2 && y < CAFE.base + 4)) continue;
      for (const sg of [-1, 1]) if (hash(q, k, sg + 5) < 0.8) put(x + sg * off, y + (sg > 0 ? 1 : 0), '#2a3054');
    }
  }
  // a second bench nearer the viewer, snow lying on its seat and back
  for (let i = 196; i <= 230; i++) { put(i, 229, SNOW[0]); put(i, 230, '#8a6440'); put(i, 231, '#5a3e28'); put(i, 235, SNOW[0]); put(i, 236, '#9a7248'); put(i, 237, '#5a3e28'); }
  for (const lx of [198, 228]) for (let y = 229; y <= 245; y++) put(lx, y, y > 237 ? '#22222c' : '#2e2e3a');
  for (const bx of BOLLARDS) {                                                         // cast-iron bollards on the coping
    const t = QY(bx) - 4;
    for (let y = t; y < t + 5; y++) for (let i = bx - 1; i <= bx + 1; i++) put(i, y, y === t ? SNOW[1] : i === bx - 1 ? '#4a4e68' : '#24263a');
    put(bx - 2, t + 1, '#3a3e58'); put(bx + 2, t + 1, '#24263a');                       // the bollard's cap lip
  }
  return p.done();
}

/* ================= The near corner: the quay café, the doughnut stall, the harbour lamps ================= */
// A two-storey wooden café on the quay at the left (red-ochre boards, white corner boards and frames), seen gable-on:
// the loft window, the upper floor with its lightbox (the billboard), the green fascia with its name, the awning, big
// warm windows and a glass door with a wreath; its right side wall runs back into the shade toward the Market Hall. On
// the quay by the water a small wooden stall sells doughnuts and coffee from a lit hatch. A harbour lamp at the edge
// and a tall cast-iron lamp near the viewer at the right; a plowed snow bank under it.
// ulkotulet: tin-can fire candles in the snow by the café's steps and the stall, as Finnish cafés light them in winter
const CANDLES = [{ x: 24, y: 233 }, { x: 52, y: 233 }, { x: 96, y: 199 }, { x: 143, y: 199 }];
const STRING = Array.from({ length: 19 }, (_, k) => {                                   // bulbs in swags along the awning
  const x = 2 + k * 4, sw = k % 4;
  return { x, y: CAFE.sign + 13 + (sw === 1 || sw === 2 ? 1 : 0), k };
});
const PLANTERS = [176, 252];
const TREE_BULBS = PLANTERS.flatMap((px0, n) => Array.from({ length: 7 }, (_, k) => ({ x: px0 + Math.round((hash(k, n, 3) - 0.5) * (k + 3) * 1.8), y: 220 + k * 3, k: k + n * 10 + 50 })));
const STALL_BULBS = Array.from({ length: 9 }, (_, k) => ({ x: STALL.x0 + 1 + k * 4, y: STALL.roof + 7 + (k & 1), k: k + 30 }));
const CAFE_WINS = [[3, 27], [49, 75]];                    // the ground floor's two windows (x spans), rows 160..216
const sideX = y => Math.round(lerp(CAFE.x1, CAFE.far, (y - CAFE.eave) / (CAFE.farEave - CAFE.eave)));
function renderCafe() {
  const p = painter(W, H), put = p.put, { x0, x1, apex, eave, sign: sg, base } = CAFE, cx = (x0 + x1) >> 1;
  // the side wall, in the shade, running back to the far corner; one lit window, the eave with its snow
  for (let x = x1; x <= CAFE.far; x++) {
    const f = (x - x1) / (CAFE.far - x1), top = Math.round(lerp(eave, CAFE.farEave, f)), bot = Math.round(lerp(base, CAFE.farBase, f));
    for (let y = top; y <= bot; y++) {
      let col = (x - x1) % 3 === 2 ? '#3a1c22' : '#4c2428';
      if (x >= x1 + 3 && x <= x1 + 7 && y >= lerp(170, 172, f) && y <= lerp(196, 190, f)) col = y < 176 ? '#ffd890' : '#e8a050';
      if (y === top) col = SNOW[1];
      if (y > bot - 3) col = '#2a2a3a';
      put(x, y, col);
    }
  }
  for (let y = 50; y < 66; y++) for (let x = 47; x <= 51; x++) put(x, y, y === 50 ? SNOW[0] : y === 51 ? '#8a8a9a' : x === 47 ? '#7a5a50' : (y & 1) ? '#5a3a34' : '#4a2e2c');   // the chimney behind the ridge
  // the gable front: boards lit warm from the windows and the lamp, darker up under the eaves
  for (let y = apex; y <= base; y++) for (let x = x0; x <= x1; x++) {
    if (y < eave && Math.abs(x - cx) > (y - apex) * (cx - x0 + 2) / (eave - apex)) continue;
    const v = 0.3 + (y - apex) / (base - apex) * 0.5 + (bayer(x, y) - 0.5) * 0.1;
    let col = x % 4 === 3 ? '#5a2628' : v > 0.66 ? '#a84a3a' : v > 0.48 ? '#8c3c32' : '#6c2e2c';
    if (x >= x1 - 2) col = x === x1 ? '#9a9088' : '#e8e0d0';                              // the white corner board
    if (y >= base - 6) col = y === base - 6 ? '#8a8a9a' : (x + y) % 5 === 0 ? '#4a4a5a' : '#5e5e70';   // granite plinth
    put(x, y, col);
  }
  for (let y = apex; y < eave; y++) {                                                   // white bargeboards, snow on them
    const hw = Math.round((y - apex) * (cx - x0 + 2) / (eave - apex));
    for (const s of [-1, 1]) { put(cx + s * hw, y, SNOW[0]); put(cx + s * (hw - 1), y, '#e8e0d0'); put(cx + s * (hw - 2), y, '#d8ccc0'); }
  }
  for (let x = x0; x <= x1; x++) { put(x, eave, '#e8e0d0'); put(x, eave + 1, '#b8ac9c'); }
  const win = (a, b, t, bt, lit = true) => {                                             // a white-framed window, warm inside
    for (let y = t; y <= bt; y++) for (let x = a; x <= b; x++) {
      const fr = x === a || x === b || y === t || y === bt, mull = (x - a) % 6 === 0 || (bt - t > 20 && y === t + 12);
      const v = (y - t) / (bt - t) + (bayer(x, y) - 0.5) * 0.2;
      put(x, y, fr ? '#efe6d6' : mull ? '#6a4630' : !lit ? '#2a2236' : v < 0.25 ? '#ffe8b4' : v < 0.65 ? '#ffc878' : '#f0a458');
    }
  };
  win(cx - 5, cx + 5, 70, 84);                                                          // the loft window in the gable
  for (let y = 70; y <= 84; y++) put(cx, y, '#efe6d6');
  for (let x = cx - 5; x <= cx + 5; x++) put(x, 77, '#efe6d6');
  win(3, 10, 106, 124); win(68, 74, 106, 124); win(32, 46, 106, 124);             // the upper floor (the middle one behind the sign)
  for (let y = sg; y <= sg + 7; y++) for (let x = x0; x <= x1 - 3; x++) put(x, y, y === sg ? '#c8a050' : y === sg + 7 ? '#8a6a3a' : '#16302a');   // the fascia
  for (let y = sg + 8; y <= sg + 12; y++) for (let x = x0; x <= x1 - 1; x++) {           // the awning, scalloped
    if (y === sg + 12 && x % 4 === 3) continue;
    put(x, y, y === sg + 8 ? SNOW[0] : (x >> 2) & 1 ? '#1e4a3a' : '#2a5e4a');
  }
  for (const [a, b] of CAFE_WINS) win(a, b, 160, 216);
  for (let y = 164; y <= 222; y++) for (let x = 31; x <= 45; x++) {                     // the glass door
    const fr = x === 31 || x === 45 || y === 164 || x === 38;
    put(x, y, fr ? '#5a3a28' : y < 190 ? '#ffd890' : '#f0b060');
  }
  for (let a = 0; a < 24; a++) { const an = a / 24 * TAU; put(38 + Math.round(Math.cos(an) * 4), 176 + Math.round(Math.sin(an) * 4), a % 5 ? '#2e5a34' : '#c83a3a'); }   // the wreath
  put(38, 181, '#d84a3a'); put(37, 182, '#d84a3a'); put(39, 182, '#d84a3a');
  for (let y = base - 5; y <= base + 3; y++) for (let x = 30 - ((y - base + 5) >> 2) * 2; x <= 46 + ((y - base + 5) >> 2) * 2; x++)
    put(x, y, (y - base + 5) % 4 === 0 ? SNOW[1] : (y - base + 5) % 4 === 1 ? '#8a8a9c' : '#5a5a6c');   // two granite steps up to the door
  // inside: the counter, pendant lamps, shelves with cups, a cake stand
  for (const [a, b] of CAFE_WINS) {
    for (let x = a + 1; x < b; x++) { put(x, 202, '#7a4a2a'); put(x, 203, '#5a3420'); }
    for (let x = a + 3; x < b - 2; x += 6) { put(x, 176, '#3a2a20'); put(x, 177, '#fff4d0'); put(x - 1, 178, '#fff0c0'); put(x + 1, 178, '#fff0c0'); }
    for (let x = a + 2; x < b - 1; x += 3) put(x, 186, hash(x, 5) < 0.5 ? '#f6ecd8' : '#c8664a');
    for (let x = a + 1; x < b; x++) put(x, 187, '#8a5a30');
  }
  for (let y = base; y <= base + 1; y++) for (let x = x0; x <= x1 + 6; x++) if (y > base || x > 50 || x < 26) put(x, y, SNOW[1]);   // snow drifted on the plinth
  // the doughnut stall: a snowy roof, its sign board, the lit hatch and counter, boards below
  const { x0: s0, x1: s1, roof: sr, base: sb } = STALL;
  for (let y = sr; y < sr + 6; y++) for (let x = s0 - 3 + (y - sr >> 1); x <= s1 + 3 - (y - sr >> 1); x++) put(x, y, y < sr + 2 ? SNOW[0] : y < sr + 4 ? SNOW[1] : '#4a3226');
  for (let y = sr + 6; y <= sb; y++) for (let x = s0; x <= s1; x++) {
    let col = (x - s0) % 4 === 0 ? '#4a2c20' : '#6a4028';
    if (y >= sr + 7 && y <= sr + 13 && x >= s0 + 4 && x <= s1 - 4) col = y === sr + 7 || y === sr + 13 ? '#c8a050' : '#24201e';   // its sign board
    if (y >= sr + 15 && y <= sr + 26 && x >= s0 + 3 && x <= s1 - 3) col = y < sr + 18 ? '#ffe2a0' : '#f4b468';           // the hatch, lit
    if (y === sr + 27 || y === sr + 28) col = y === sr + 27 ? '#d8b080' : '#8a5a34';                                     // the counter
    if (x === s1) col = '#3a2018';
    put(x, y, col);
  }
  for (let x = s0 + 22; x <= s0 + 26; x++) for (let y = sr + 21; y <= sr + 26; y++) put(x, y, x === s0 + 22 ? '#d8d8e0' : '#a8a8b8');   // the coffee urn
  for (let x = s0 + 6; x <= s0 + 14; x += 2) { put(x, sr + 26, '#d8964a'); put(x + 1, sr + 26, '#e8b060'); }                             // munkit on a tray
  // the harbour lamp at the quay's edge and the tall cast-iron lamp near the viewer
  for (const L of [...LAMPS, NEAR_LAMP]) {
    const big = L === NEAR_LAMP, w = big ? 3 : 2, lw = big ? 6 : 3;
    for (let y = L.top + 8; y <= L.base; y++) for (let i = 0; i < w; i++) put(L.x + i, y, i === 0 ? '#4a4e66' : '#1e2032');
    for (let y = L.base - (big ? 8 : 3); y <= L.base; y++) for (let i = L.x - (big ? 3 : 2); i <= L.x + w + (big ? 2 : 1); i++) put(i, y, y === L.base - (big ? 8 : 3) ? '#5a5e76' : '#2a2c40');
    for (let i = L.x - lw; i <= L.x + w + lw - 1; i++) { put(i, L.top, SNOW[0]); put(i, L.top + 1, '#2a2c40'); }
    for (let y = L.top + 2; y <= L.top + (big ? 10 : 6); y++) for (let i = L.x - lw + 1; i <= L.x + w + lw - 2; i++)
      put(i, y, i === L.x - lw + 1 || i === L.x + w + lw - 2 || (big && i === L.x + 1) ? '#2a2c40' : y < L.top + 4 ? '#fff6dc' : '#ffdc9a');
    for (let i = L.x - lw + 2; i <= L.x + w + lw - 3; i++) put(i, L.top + (big ? 11 : 7), '#2a2c40');
  }
  // planters of small spruces along the quay, snow on their boughs (their bulbs twinkle per frame)
  for (const px0 of PLANTERS) {
    for (let y = 240; y <= 248; y++) for (let x = px0 - 9; x <= px0 + 9; x++) put(x, y, y === 240 ? SNOW[0] : y === 241 ? '#a88a64' : (x - px0 + 9) % 6 === 0 ? '#3a2618' : y > 246 ? '#2a1a14' : '#5a3a26');
    for (let j = 0; j < 24; j++) {
      const hw = Math.round(j / 24 * 8) + (j % 5 === 4 ? 0 : 1), y = 216 + j;
      for (let i = -hw; i <= hw; i++) put(px0 + i, y, j % 5 === 0 && Math.abs(i) < hw ? SNOW[1] : (i + j) % 3 === 0 ? '#2e6a4a' : i < 0 ? '#245a3e' : '#1a4430');
    }
    put(px0, 215, SNOW[0]);
  }
  // the plowed snow bank under the near lamp, a shovel stuck in it
  for (let x = 330; x < W; x++) {
    const h = Math.round(12 * Math.sin(Math.PI * clamp((x - 330) / 160, 0, 1)) ** 0.7 + 2 * Math.sin(x * 0.21) + 3 * vnoise(x, 0, 14, 8));
    for (let y = 262 - h; y < 266; y++) {
      const top = y < 262 - h + 2, lit = Math.max(0, 1 - Math.abs(x - NEAR_LAMP.x) / 60);
      put(x, y, top ? (lit > 0.3 ? '#fff0d8' : SNOW[0]) : ramp(lit > 0.2 ? WARMSNOW : COOLSNOW, 0.75 - (y - 262 + h) / 18 + lit * 0.3, x, y));
    }
  }
  for (let y = 238; y < 252; y++) put(372 + ((y - 238) >> 3), y, '#6a4a2a');
  for (let y = 236; y < 240; y++) for (let x = 370; x <= 375; x++) put(x, y, y === 236 ? '#5a6a8a' : '#3a4a6a');
  const c = p.done();
  sign(p.x, 'KAHVILA', 22, sg + 2, '#ffd88a');
  sign(p.x, 'MUNKIT', s0 + 6, sr + 8, '#ffd88a');
  return c;
}
// figures inside the café, seen through the glass: two at a table by the window, the waiter going to and fro
function drawCafeInside(t, crowd) {
  if (crowd <= 0) return;
  ctx.save(); ctx.beginPath();
  for (const [a, b] of CAFE_WINS) ctx.rect(a + 1, 161, b - a - 1, 55);
  ctx.rect(32, 165, 13, 57); ctx.clip();
  const sip = frac(t / 16) < 0.2, lean = frac(t / 30 + 0.4) < 0.3;
  R(6, 192, 5, 10, '#5a3a40'); R(7, 186 + (lean ? 1 : 0), 4, 5, '#c89a7a'); R(7, 186 + (lean ? 1 : 0), 4, 2, '#3a2418');
  R(17, 192, 5, 10, '#3a4a6a'); R(17, 186, 4, 5, '#d8aa88'); R(17, 186, 4, 1, '#c8a058');
  if (sip) R(15, 188, 2, 2, '#f0e8d8'); else R(13, 199, 2, 2, '#f0e8d8');
  R(9, 200, 11, 1, '#6a4a2a');
  const wx = Math.round(lerp(52, 72, 0.5 + 0.5 * wave(t, 6, 0.2))), wd = wave(t, 6, 0.45) > 0;
  R(wx, 184, 5, 14, '#2a2a30'); R(wx + 1, 178, 4, 6, '#d0a080'); R(wx + 1, 178, 4, 1, '#1e1814'); R(wx + 1, 190, 3, 8, '#efe8dc');
  if (wd) R(wx + 5, 186, 3, 1, '#c8c8d0');
  ctx.restore();
}

/* ================= The billboard: a lightbox on the café's upper floor, an ad for hot glögi ================= */
function renderBillboardAd() {
  const { w, h } = BB, p = painter(w, h), put = p.put;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) put(x, y, ramp(['#6e1a2a', '#5e1624', '#4c121e', '#3a0e18'], y / h * 0.7 + Math.abs(x - w / 2) / w * 0.5, x, y));
  for (let x = 0; x < w; x++) { put(x, 0, '#e8b860'); put(x, h - 1, '#a87a3a'); }
  const MUG = ['.s.s.', 's.s..', '.s.s.', '.....', 'gggg.', 'gwwgg', 'gwwg.g', 'gwwgg', 'gggg.'];   // a glass mug, glögi glowing in it
  MUG.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] !== '.') put(3 + i, 6 + j, { s: '#f0e4d8', g: '#f2e8dc', w: '#e04a3a' }[row[i]]); });
  const c = p.done();
  const [g, gx] = makeCanvas(w, h);                                  // GLÖGI at twice the size, in gold
  sign(gx, 'GLÖGI', 0, 1, '#ffd27a');
  p.x.imageSmoothingEnabled = false;
  p.x.drawImage(g, 0, 0, 20, 6, 11, 1, 40, 12);
  sign(p.x, 'KUUMA', 21, 14, '#f8e8d0');
  return gradeAd(c, { level: 0.9, tint: '#141a3c', mix: 0.05, wear: 0.06, seed: 61 });
}
function drawBillboard(t, pitch) {
  R(BB.x - 2, BB.y - 2, BB.w + 4, BB.h + 4, '#1a1a26'); R(BB.x - 2, BB.y - 2, BB.w + 4, 1, '#5a5a6c');
  for (const bx of [BB.x + 4, BB.x + BB.w - 5]) R(bx, BB.y + BB.h + 2, 1, 3, '#2a2a38');   // its brackets
  if (!PITCH.covers(t, pitch)) ctx.drawImage(BB_AD, BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  R(BB.x - 2, BB.y - 3, BB.w + 4, 1, SNOW[0]);
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), 0.45);
}

/* ================= A small motorboat laid up for winter, moored along the quay's right-hand edge ================= */
const BOAT = { x0: 404, x1: 462, wl: 196 };
function renderBoat() {
  const p = painter(W, H), put = p.put, { x0, x1, wl } = BOAT;
  for (let x = x0; x <= x1; x++) {                                                      // the hull: white topsides, a blue boot-top
    const f = (x - x0) / (x1 - x0), sheer = Math.round(3 * (1 - f) ** 2), keel = x < x0 + 4 ? x0 + 4 - x : 0;
    for (let y = wl - 6 - sheer; y <= wl - keel * 0; y++) put(x, y, y === wl - 6 - sheer ? '#e8ecf4' : y >= wl - 1 ? '#2a4a7a' : y === wl - 2 ? '#c8ccd8' : x > x1 - 6 ? '#9aa0b4' : '#c8ccdc');
  }
  for (let x = x0 + 6; x <= x1 - 3; x++) {                                              // the winter cover, humped, snow on it
    const f = (x - x0 - 6) / (x1 - 3 - x0 - 6), h = Math.round(7 * Math.sin(Math.PI * Math.min(1, f * 1.15)) ** 0.6);
    for (let y = wl - 7 - h; y <= wl - 7; y++) put(x, y, y <= wl - 7 - h + 1 ? SNOW[0] : (x % 6 === 0 ? '#1e4a3a' : '#2a5e4a'));
  }
  for (let x = x0; x <= x1; x++) put(x, wl + 1, '#141a34');                             // its shadow on the water
  for (let k = 0; k <= 12; k++) put(lerp(x0 - 1, BOLLARDS[4] + 1, k / 12), wl - 5 + Math.round(Math.sin(Math.PI * k / 12) * 2), '#c8b890');   // the mooring rope
  for (let k = 0; k <= 8; k++) put(lerp(x1, BOLLARDS[5] - 1, k / 8), wl - 4 + Math.round(Math.sin(Math.PI * k / 8) * 1), '#c8b890');
  return p.done();
}

/* ================= People: dressed for a snowy night, lit by the café and the lamps ================= */
const SKINS = ['#f2c8a0', '#e0aa80', '#c08a60', '#8a5a3c'], HAIRS = ['#1c1412', '#3c2418', '#6c4222', '#d8b878', '#8e3e1c', '#c8c4c0'];
const PNIGHT = '#141a3a', GLOW = '#ffd8a0';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.2, m = c => mixHex(c, PNIGHT, dk);
  const hh = h >= 20 ? 5 : 4, th = Math.round(h * (o.coat ? 0.5 : 0.4));
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 21 ? 7 : h >= 18 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOW, 0.3), top: m(top), topD: mixHex(top, PNIGHT, dk + 0.28),
    topL: mixHex(top, GLOW, 0.3), bot: m(bot), shoe: m('#1e1a20'), long: o.long || 0,
    hat: o.hat ? m(o.hat) : null, scarf: o.scarf ? m(o.scarf) : null, beanie: o.beanie ? m(o.beanie) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the row of the top of the head.
 * o.view: 0 from behind, 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame; o.sit: seated at fy
 * o.aL / o.aR: 0 down, 2 camera or phone up, 3 out, 4 mug raised, 5 hand to the face
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll;
  const ty = fy - legs - th + 1, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1);
  if (legs) {
    const ly = fy - legs + 1;
    if (side) {
      if (o.step) {
        R(dir > 0 ? x + 1 : x - 2, ly, 2, legs, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly, 2, legs - 1, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, lw, legs, P.bot); R(tx + bw - lw, ly, lw, legs, P.bot);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) {
    R(tx - 1, fy - 1, bw + 2, 2, P.bot);
    if (view === 1) { R(tx, fy + 1, 2, 5, P.bot); R(tx + bw - 2, fy + 1, 2, 5, P.bot); R(tx, fy + 6, 2, 1, P.shoe); R(tx + bw - 2, fy + 6, 2, 1, P.shoe); }
  }
  R(tx, ty, bw, th, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th - 1, P.topD);
  R(tx, ty, bw, 1, P.topL);
  if (P.scarf) R(tx, ty, bw, 2, P.scarf);
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
    else if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(sg < 0 ? ax - 1 : ax, hy, 2, 2, '#16161e'); }
    else if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(ax, ty, 1, 3, P.topD); R(ax, ty - 1, 1, 1, P.skin); R(ax + (sg < 0 ? -1 : 0), ty - 3, 2, 2, '#f0e8d8'); }
    else { R(ax, ty, 1, 2, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); }
  };
  if (side) {
    const st = o.aR || 0;
    if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); }
    else if (st === 4) { R(x + dir, ty, 1, 3, P.topD); R(x + dir * 2, ty - 2, 2, 2, '#f0e8d8'); }
    else if (st === 2) { R(x + dir, ty, 1, 2, P.topD); R(x + dir * 2, hy + 1, 2, 3, '#16161e'); }
    else { const ax = x + (o.step ? -dir : 0); R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  } else { arm(tx - 1, o.aL, -1); arm(tx + bw, o.aR, 1); }
  const hx = x - (hw >> 1) + (o.lean || 0), hr = hh >= 5 ? 2 : 1;
  if (view === 0) {
    R(hx, hy, hw, hh, P.hair); R(hx, hy, hw, 1, P.hairL);
    if (P.long) R(hx, hy + hh, hw, P.long, P.hair); else R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hy + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hy + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hy, hw, hh, P.skin); R(hx, hy, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hy, 1, hh - 1 + P.long, P.hair); }
  if (P.hat) { R(hx, hy - 1, hw, 2, P.hat); R(hx - 1, hy, hw + 2, 1, P.hat); }
  if (P.beanie) { R(hx, hy - 1, hw, 2, P.beanie); R(hx + (hw >> 1), hy - 2, 1, 1, '#e8e2d4'); }
  return hy;
}
const shadow = (x, fy, w) => { ctx.globalAlpha = 0.45; R(Math.round(x) - (w >> 1), Math.round(fy) + 1, w, 1, '#0a0a18'); ctx.globalAlpha = 1; };
const rk = i => frac((i + 0.5) * 0.618);
const BENCH = { x0: 298, x1: 330, y: 206 };
function renderBench() {
  const p = painter(W, H), put = p.put, { x0, x1, y } = BENCH;
  for (let i = x0; i <= x1; i++) {
    put(i, y - 9, SNOW[1]); put(i, y - 8, '#8a6440'); put(i, y - 7, '#5a3e28'); put(i, y - 5, '#7a5636'); put(i, y - 4, '#4a3222');
    put(i, y - 1, SNOW[0]); put(i, y, '#9a7248'); put(i, y + 1, '#5a3e28');
  }
  for (const lx of [x0 + 2, x1 - 2]) for (let yy = y - 9; yy <= y + 7; yy++) put(lx, yy, yy > y + 1 ? '#22222c' : '#2e2e3a');
  return p.done();
}
/* ================= The cast: people who stay, each with a verb, and people walking the quay =================
   rank: drawn while rank < crowd (Settings, People); rank 0 is there at every level above 0 (the stall's vendor and
   its two customers, the café's guests, the family with the sled); pairs share one rank                          */
const TOPS = ['#8a2a34', '#2a4a7a', '#c8a050', '#3a6a5a', '#5a3a6a', '#b0503a', '#4a4a58', '#d8c8a8', '#2a5a8a', '#7a5a3a'];
const BOTS = ['#22202c', '#33466e', '#3a3a48', '#4a3a30'];
const CAST = [
  { id: 'buyer', x: 146, y: 200, P: look(21, 0, 3, TOPS[0], BOTS[0], { long: 3, beanie: '#e8e0d0', coat: true }), o: { view: 2, dir: -1 }, rank: 0 },
  { id: 'friend', x: 156, y: 201, P: look(22, 1, 1, TOPS[3], BOTS[1], { scarf: '#c8a050', beanie: '#2a3a5a' }), o: { view: 2, dir: -1 }, rank: 0 },
  { id: 'herEdge', x: 206, y: 194, P: look(18, 0, 2, TOPS[7], BOTS[0], { long: 2, coat: true, scarf: '#8a2a34', dk: 0.28 }), o: { view: 0 }, rank: rk(3) },
  { id: 'himEdge', x: 212, y: 194, P: look(19, 1, 0, TOPS[1], BOTS[2], { beanie: '#2a3a5a', coat: true, dk: 0.28 }), o: { view: 0 }, rank: rk(3) },
  { id: 'photo', x: 270, y: 200, P: look(22, 2, 0, TOPS[6], BOTS[0], { beanie: '#c84a3a', coat: true }), o: { view: 0, aL: 2, aR: 2 }, rank: rk(5) },
  { id: 'sitter', x: 314, y: BENCH.y, P: look(23, 1, 5, TOPS[9], BOTS[3], { hat: '#2a2226', scarf: '#e8d8b0', coat: true }), o: { view: 0, sit: true }, rank: rk(8) },
  { id: 'rail', x: 372, y: 197, P: look(19, 3, 0, TOPS[2], BOTS[1], { beanie: '#8a2a34', coat: true, dk: 0.26 }), o: { view: 0, aR: 5 }, rank: rk(10) },
];
const P_VENDOR = look(20, 1, 3, '#2a5a4a', BOTS[0], { beanie: '#c84a3a', dk: 0.05 });
const P_PARENT = look(23, 0, 2, TOPS[8], BOTS[0], { coat: true, beanie: '#e8c040' }), P_KID = look(12, 0, 3, '#d84a6a', BOTS[1], { beanie: '#3a8ad8', dk: 0.15 });
const P_DOGGER = look(24, 2, 0, TOPS[4], BOTS[0], { beanie: '#3a6a5a', coat: true });
// walkers on the quay: [t0, t1, from x, to x, lane]; they cross whole, in and out at the frame's edges or the café
const SLED_T = [[8, 68, 92, 500], [128, 188, 500, 92]];   // the parent pulls the child on a sled along the quay and back
const DOG_T = [[70, 118, 500, -20], [196, 236, -20, 500]];
const walkAt = (list, t) => {
  const w = list.find(([a, b]) => t >= a && t < b);
  if (!w) return null;
  const u = (t - w[0]) / (w[1] - w[0]);
  return { x: lerp(w[2], w[3], u), dir: w[3] > w[2] ? 1 : -1, a: Math.min(1, smooth(0, 0.04, u) * 4, (1 - u) * 25) };
};
function drawSledFamily(t) {
  const w = walkAt(SLED_T, t);
  if (!w) return;
  const st = step(t, 768) & 1, x = Math.round(w.x), y = 207, d = w.dir;
  ctx.globalAlpha = clamp(w.a, 0, 1);
  shadow(x, y, 8);
  const hy = person(x, y, P_PARENT, { view: 2, dir: d, step: st });
  const sx = x - d * 16;                                           // the wooden sled behind on its rope, the child on it
  ctx.fillStyle = '#9a96a8';
  for (let k = 3; k < 13; k += 2) ctx.fillRect(x - d * k, hy + 13 + (k >> 2), 1, 1);
  R(sx - 6, y - 1, 13, 1, '#c88a4a'); R(sx - 6, y, 13, 1, '#5a3420'); R(sx + d * 6, y - 3, 1, 3, '#c88a4a');
  person(sx - d, y - 2, P_KID, { view: 2, dir: d, sit: true });
  ctx.globalAlpha = 1;
}
function drawDogWalker(t) {
  const w = walkAt(DOG_T, t);
  if (!w) return;
  const st = step(t, 768) & 1, x = Math.round(w.x), y = 206, d = w.dir, dx = x + d * 14;
  shadow(x, y, 8);
  person(x, y, P_DOGGER, { view: 2, dir: d, step: st });
  ctx.fillStyle = '#5a4a3a';                                       // the lead, and a black-and-tan dog trotting ahead
  for (let k = 3; k < 13; k += 2) ctx.fillRect(x + d * k, y - 11 + (k >> 1), 1, 1);
  R(dx - 4, y - 5, 8, 3, '#2a221e'); R(dx + d * 4 - (d < 0 ? 2 : 0), y - 7, 3, 3, '#2a221e'); R(dx + d * 5, y - 5, 1, 1, '#a8703a');
  R(dx - d * 4, y - 7, 1, 2, '#2a221e');
  for (const [lx, ph] of [[-3, 0], [-1, 1], [2, 0], [3, 1]]) R(dx + lx, y - 2, 1, 2 - ((st + ph) & 1), '#2a221e');
}
function drawVendor(t) {                                           // in the hatch: pours coffee, hands over a bag of munkit
  const x = 114, pour = frac(t / 20) < 0.35;
  ctx.save(); ctx.beginPath(); ctx.rect(STALL.x0 + 3, STALL.roof + 15, STALL.x1 - STALL.x0 - 6, 12); ctx.clip();
  const hy = person(x, STALL.roof + 34, P_VENDOR, { view: 1, aR: pour ? 3 : 0, aL: !pour && frac(t / 20) < 0.6 ? 3 : 0 });
  ctx.restore();
  void hy;
}
function drawCast(t, crowd) {
  const DL = [];
  for (const c of CAST) if (c.rank < crowd) DL.push([c.y, () => {
    shadow(c.x, c.y, 8);
    const o = { ...c.o };
    if (c.id === 'buyer') o.aR = frac(t / 20 + 0.3) < 0.3 ? 3 : 0;                    // reaches for the bag at the hatch
    if (c.id === 'friend') o.aR = frac(t / 12) < 0.25 ? 4 : 0;                          // sips her coffee
    if (c.id === 'himEdge' && frac(t / 40) < 0.2) o.aR = 3;                            // points out the wheel to her
    const hy = person(c.x, c.y, c.P, o);
    if (c.id === 'photo') {                                                            // the tripod and the camera on it
      ctx.fillStyle = '#2a2a34';
      for (let k = 0; k < 12; k++) { ctx.fillRect(c.x + 5 - (k >> 2), c.y - 11 + k, 1, 1); ctx.fillRect(c.x + 5 + (k >> 2), c.y - 11 + k, 1, 1); }
      R(c.x + 3, hy + 2, 4, 3, '#16161e'); R(c.x + 4, hy + 2, 1, 1, '#8ad8e8');
    }
  }]);
  if (crowd > 0) { DL.push([STALL.base, () => drawVendor(t)]); DL.push([207, () => drawSledFamily(t)]); }
  if (rk(6) < crowd) DL.push([206.5, () => drawDogWalker(t)]);
  DL.push([SNOWMAN.y, () => drawSnowman(t)]);
  if (crowd > 0) DL.push([210, () => { breath(150, 181, t, 0.1); breath(158, 180, t, 0.55); if (rk(3) < crowd) breath(214, 176, t, 0.3); }]);
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, draw] of DL) draw();
}

/* ================= The SkyWheel's turning parts: rim, spokes, 30 gondolas; one gondola spacing per loop ================= */
const GONDOLAS = 30, WHEEL_NF = 24;
function renderWheelFrames() {
  const frames = [], { r } = WHEEL, s = r * 2 + 5;
  for (let f = 0; f < WHEEL_NF; f++) {
    const p = painter(s, s), put = p.put, c = r + 2, rot = f / WHEEL_NF * TAU / GONDOLAS;
    for (let a = 0; a < 360; a++) { const an = a / 360 * TAU; put(c + Math.cos(an) * r, c + Math.sin(an) * r, '#c8d0e4'); put(c + Math.cos(an) * (r - 2), c + Math.sin(an) * (r - 2), '#8a94b4'); }
    for (let k = 0; k < GONDOLAS / 2; k++) {
      const an = rot + k * TAU / (GONDOLAS / 2);
      for (let q = 2; q < r - 1; q++) if (q % 2 === 0 || q > r - 5) put(c + Math.cos(an) * q, c + Math.sin(an) * q, '#6a7494');
    }
    for (let i = -2; i <= 2; i++) for (let j = -2; j <= 2; j++) if (Math.abs(i) + Math.abs(j) <= 3) put(c + i, c + j, i + j < 0 ? '#e8ecf6' : '#9aa2bc');
    frames.push(p.done());
  }
  return frames;
}
function drawWheel(t, show) {
  const { cx, cy, r } = WHEEL, u = frac(t / LOOP), f = Math.floor(u * WHEEL_NF) % WHEEL_NF;
  ctx.drawImage(WHEEL_C[f], cx - r - 2, cy - r - 2);
  const rot = u * TAU / GONDOLAS;
  for (let k = 0; k < GONDOLAS; k++) {
    const an = rot + k * TAU / GONDOLAS, gx = Math.round(cx + Math.cos(an) * r), gy = Math.round(cy + Math.sin(an) * r) + 1;
    const col = show ? ['#ff6ab0', '#6ad8ff', '#ffe06a', '#8aff9a', '#b88aff'][(k + step(t, 960)) % 5] : (k % 10 === 3 ? '#ffd890' : '#b8d8ff');   // the sauna gondola glows warm
    R(gx - 1, gy, 2, 2, col);
  }
  halo(HL.wheel, cx, cy, show ? 0.9 : 0.55);
}

/* ================= The loop's timeline (seconds) =================
   t          what
   all loop   the SkyWheel turns one gondola spacing; steam off Allas, swimmers doing lengths, a bather on the deck;
              windows switch on and off along the waterfront, Uspenski's warehouses and Eteläranta; the café's guests
              talk and sip; the vendor pours coffee and hands over munkit; candles flicker; breath steams in the cold
   8-68       a parent pulls a child on a sled along the quay to the right (back 128-188)
   20-50      a tram runs along the Market Square to the right (again 150-180 to the left)
   28-50      three gulls glide over the harbour (again 108-128, 212-232)
   40-62      a seal puts its head up by the quay (again 164-186): a hidden thing
   48-66      Uspenski's bells: the belfry warms (48), four strokes (52, 55, 58, 61)
   70-118     a dog walker crosses the quay to the left (back 196-236)
   80-140     the Suomenlinna ferry: its deck lights brighten at the Market Square (80), the horn (86), it backs out,
              turns and runs down the harbour toward the quay and out to the right, its wake spreading (86-140)
   146-172    the snow thickens to a flurry and eases
   182-210    the SkyWheel's colour show: the gondolas' lights run through the colours and chase round the rim  */
const TRAMS = [{ a: 20, b: 50, dir: 1 }, { a: 150, b: 180, dir: -1 }];
const SHOW = { a: 182, b: 210 };
const BELLS = [52, 55, 58, 61], BELL_GLOW = { a: 48, b: 66 };
const FERRY = { lit: 80, horn: 86, t1: 140 };
const FLURRY = { a: 146, b: 172, f: 8 };
const GULL_T = [{ a: 28, b: 50, dir: -1, y: 74 }, { a: 108, b: 128, dir: 1, y: 60 }, { a: 212, b: 232, dir: -1, y: 86 }];
const SEAL = { x: 366, y: 184, at: [[40, 62], [164, 186]] };
const SNOWMAN = { x: 424, y: 203 }, CAT = { x: 71, y: 124 };
const bellRing = t => BELLS.reduce((m, b) => Math.max(m, t >= b && t < b + 2.5 ? 1 - (t - b) / 2.5 : 0), 0);
const flurry = t => env(t, FLURRY.a, FLURRY.b, FLURRY.f);
// windows that switch on and off (filled in while the waterfront is drawn)
const WINS = [], FAR_WINS = [];
function drawWins(t, list = WINS) {
  for (const w of list) if (hash(w.id, Math.floor(frac(t / LOOP + w.ph) * w.n), 13) < 0.5) { R(w.x, w.y, w.w, 1, WARMW[0]); if (w.h > 1) R(w.x, w.y + 1, w.w, w.h - 1, WARMW[1]); }
}
// the Suomenlinna ferry: dark hull with a red boot-top, white decks with warm windows, a funnel, its mast light
function ferryAt(t) {
  if (t < FERRY.lit - 6 || t >= FERRY.t1) return null;
  const u = clamp((t - FERRY.horn) / (FERRY.t1 - FERRY.horn), 0, 1), e = u * u * (3 - 2 * u) * 0.35 + u * 0.65;
  return { x: lerp(290, 530, e), y: lerp(158.5, 189, Math.pow(e, 1.4)), s: lerp(0.42, 1.15, e), moving: u > 0, lit: smooth(FERRY.lit - 6, FERRY.lit, t), u };
}
function drawFerry(t) {
  const f = ferryAt(t);
  if (!f) return;
  const s = f.s, x = Math.round(f.x), y = Math.round(f.y), L = Math.max(10, Math.round(36 * s)), Hh = Math.max(2, Math.round(4 * s)), Sh = Math.max(2, Math.round(6 * s));
  if (f.moving) {                                                // the wake: two pale lines spreading behind it, fading
    ctx.globalAlpha = 0.35;
    for (let k = 2; k < 28 * s; k++) { const sp = Math.round(k * 0.45); R(x - L / 2 - k, y + 1 - (k >> 3), 1, 1, '#a8b8d8'); R(x - L / 2 - k + sp, y + 1 + Math.round(k * 0.12), 1, 1, '#8898c0'); }
    ctx.globalAlpha = 1;
  }
  const x0 = Math.round(x - L / 2);
  ctx.globalAlpha = 0.25; R(x0, y + 1, L, Hh, '#2a3050'); ctx.globalAlpha = 1;      // its mirror in the water
  R(x0 + 1, y - Hh, L - 2, Hh, '#1c2440'); R(x0, y - Hh, L, 1, '#c8c8d8'); R(x0 + 1, y - 1, L - 2, 1, '#a83a3a');   // hull, rail, boot-top
  R(x0 + Math.round(L * 0.18), y - Hh - Sh, Math.round(L * 0.62), Sh, '#e8e8ee');                                      // the decks
  const wy = y - Hh - Sh + Math.max(1, Math.round(Sh * 0.35));
  for (let i = x0 + Math.round(L * 0.2); i < x0 + Math.round(L * 0.78); i += Math.max(2, Math.round(3 * s))) R(i, wy, Math.max(1, Math.round(s * 1.5)), Math.max(1, Math.round(s * 1.5)), f.lit > 0.5 ? '#ffd890' : '#6a6a80');
  R(x0 + Math.round(L * 0.5), y - Hh - Sh - Math.max(2, Math.round(4 * s)), Math.max(1, Math.round(3 * s)), Math.max(2, Math.round(4 * s)), '#2a2a3a');   // funnel
  const mx = x0 + Math.round(L * 0.35), my = y - Hh - Sh - Math.round(7 * s);
  R(mx, my, 1, Math.round(7 * s), '#3a3a4a'); R(mx, my - 1, 1, 1, '#ffffff'); halo(HL.tram, mx, my - 1, 0.7 * f.lit);
  R(x0 + L - 1, y - Hh, 1, 1, '#4aff8a'); halo(HL.nav, x0 + L - 1, y - Hh, 0.7 * f.lit);
  halo(HL.ferry, x, wy, 0.5 * f.lit);
}
function drawGulls(t) {                                          // white gulls lit from under by the city, gliding
  for (const g of GULL_T) {
    if (t < g.a || t >= g.b) continue;
    const u = (t - g.a) / (g.b - g.a);
    for (let k = 0; k < 3; k++) {
      const x = Math.round(g.dir > 0 ? lerp(-20, 500, u) - k * 14 : lerp(500, -20, u) + k * 14), y = Math.round(g.y + k * 5 + wave(t, 40 + k * 8, k * 0.3) * 2);
      const up = (step(t, 480) + k) % 3 === 0;
      R(x - 1, y, 3, 1, '#e8ecf4'); R(x - 3, y - (up ? 1 : 0), 2, 1, '#c8ccd8'); R(x + 2, y - (up ? 1 : 0), 2, 1, '#c8ccd8');
      if (!up) { R(x - 4, y + 1, 1, 1, '#a8acc0'); R(x + 4, y + 1, 1, 1, '#a8acc0'); }
    }
  }
}
// the hidden things: a seal by the quay (twice), a snowman on the quay's edge, the café's cat in the upper window
function drawSeal(t) {
  const w = SEAL.at.find(([a, b]) => t >= a && t < b);
  if (!w) return;
  const u = (t - w[0]) / (w[1] - w[0]), up = smooth(0, 0.15, u) * (1 - smooth(0.85, 1, u)), h = Math.round(up * 4), look = wave(t, 12) > 0 ? 1 : 0;
  if (h <= 0) return;
  R(SEAL.x - 2, SEAL.y - h, 4, h, '#4a4a58'); R(SEAL.x - 2, SEAL.y - h, 4, 1, '#7a7a8a'); R(SEAL.x - 1 + look, SEAL.y - h + 1, 1, 1, '#0e0e14');
  ctx.globalAlpha = 0.5 * up; R(SEAL.x - 4, SEAL.y, 8, 1, '#a8b8d8'); R(SEAL.x - 6, SEAL.y + 1, 2, 1, '#8898c0'); R(SEAL.x + 4, SEAL.y + 1, 2, 1, '#8898c0'); ctx.globalAlpha = 1;
}
function drawSnowman(t) {
  const { x, y } = SNOWMAN;
  R(x - 4, y - 5, 9, 5, '#dfe4f4'); R(x - 3, y - 6, 7, 1, '#dfe4f4'); R(x + 3, y - 5, 2, 5, '#b8c0dc');
  R(x - 3, y - 11, 7, 5, '#e8ecf8'); R(x + 2, y - 11, 2, 5, '#c0c8e0');
  R(x - 2, y - 14, 5, 3, '#2a2a3a'); R(x - 3, y - 12, 7, 1, '#2a2a3a');                       // a dark woollen hat
  R(x - 3, y - 7, 7, 1, '#c83a3a'); R(x + 3, y - 6, 1, 2, '#c83a3a');                         // a red scarf
  R(x - 1, y - 9, 1, 1, '#1a1a22'); R(x + 1, y - 9, 1, 1, '#1a1a22'); R(x, y - 8, 2, 1, '#ff8a3a');
  if (frac(t / 30) < 0.5) R(x - 6, y - 8, 2, 1, '#5a4030'); else R(x - 6, y - 9, 2, 1, '#5a4030');
}
function drawCat(t) {
  const { x, y } = CAT, flick = frac(t / 7.5) < 0.2;
  R(x - 2, y - 4, 4, 4, '#2a2024'); R(x - 1, y - 7, 3, 3, '#2a2024'); R(x - 1, y - 8, 1, 1, '#2a2024'); R(x + 1, y - 8, 1, 1, '#2a2024');
  R(x + 2, y - (flick ? 3 : 1), 1, flick ? 3 : 1, '#2a2024'); if (!flick) R(x + 3, y - 1, 1, 1, '#2a2024');
  R(x, y - 6, 1, 1, '#c8e08a');
}
function breath(x, y, t, ph) {                                   // a puff of breath in the cold every few seconds
  const { u } = cycle(t, 60, ph);
  if (u < 0.35) halo(HL.breath, x + u * 8, y - u * 6, 0.6 * (1 - u / 0.35));
}

/* ================= Helsinki's own snow at three depths ================= */
// far flakes small and slow, falling behind the cathedrals onto the far roofs; mid ones over the harbour, melting on
// the water; near ones, bigger, drifting past everything. Flakes near a lamp, the café or the Cathedral catch its light.
const FLAKES = [
  { n: 340, nMin: 16, nMax: 24, size: 1, col: '#a8b0d8', lit: '#e8e4f0', a: 0.55, land: [60, 160], sway: 3 },
  { n: 320, nMin: 26, nMax: 36, size: 1, col: '#ccd4f2', lit: '#ffe4c0', a: 0.75, land: [150, 205], sway: 5 },
  { n: 160, nMin: 38, nMax: 54, size: 2, col: '#eef2ff', lit: '#fff0d8', a: 0.85, land: [205, H + 6], sway: 8 },
].map((L, d) => ({ ...L, flakes: Array.from({ length: Math.round(L.n * 1.5) }, (_, i) => ({ n: L.nMin + Math.floor(hash(i, d, 71) * (L.nMax - L.nMin)), ph: hash(i, d, 72), s: i * 7 + d, k: 12 + Math.floor(hash(i, d, 75) * 24), extra: i >= L.n })) }));
const SNOW_LIGHTS = [{ x: 40, y: 192, r: 50 }, { x: 119, y: 181, r: 24 }, ...LAMPS.map(L => ({ x: L.x, y: L.top + 5, r: 26 })),
  { x: NEAR_LAMP.x + 1, y: NEAR_LAMP.top + 6, r: 36 }, { x: CATH.cx, y: 104, r: 56, col: '#f4f6ff' }];
const SNOW_LIGHTS_BB = [...SNOW_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 10, r: 30, col: '#ffb0a0' }];
const litCol = (x, y, lights) => { for (const l of lights) if ((x - l.x) ** 2 + (y - l.y) ** 2 < l.r * l.r) return l.col || null; return undefined; };
function drawSnow(depth, t, lights) {
  const L = FLAKES[depth];
  ctx.globalAlpha = L.a;
  const wind = flurry(t);
  for (const f of L.flakes) {
    const { cyc, u } = cycle(t, f.n, f.ph);
    if (f.extra && hash(f.s, cyc, 76) > flurry(t - u * LOOP / f.n) * 1.1) continue;     // the flurry's flakes decide at the top
    const land = lerp(L.land[0], L.land[1], hash(f.s, cyc, 73));
    const x0 = hash(f.s, cyc, 74) * (W + 60) - 30, y = Math.round(lerp(-6, land, u));
    const x = Math.round(x0 + Math.sin(TAU * (f.k * t / LOOP) + f.s) * L.sway * 0.5 - u * L.sway - wind * (y + 6) * 0.3);
    const lc = litCol(x, y, lights);
    ctx.fillStyle = lc === undefined ? L.col : lc || L.lit;
    ctx.fillRect(x, y, L.size, L.size);
  }
  ctx.globalAlpha = 1;
}
// fresh snow on the quay glitters where the lamps and the café light it (only while it is the city's own snow falling)
const GLINTS = [];
{
  const r = rng(77);
  while (GLINTS.length < 520) {
    const x = Math.floor(r() * W), y = 204 + Math.floor(r() * 64);
    if (y < QY(x) + 3 || (x < CAFE.far + 2 && y < CAFE.base + 6) || (x > 330 && y > 246) || PLANTERS.some(p0 => Math.abs(x - p0) < 11 && y < 250)) continue;
    GLINTS.push({ x, y, w: warmAt(x, y) });
  }
}
function drawGlints(t) {
  const s = step(t, 1200);
  for (let i = 0; i < GLINTS.length; i++) {
    if (hash(i, s, 43) > 0.2) continue;
    const g = GLINTS[i];
    ctx.globalAlpha = 0.35 + 0.4 * Math.min(1, g.w); R(g.x, g.y, 1, 1, g.w > 0.15 ? '#fff4d8' : '#e8eeff');
  }
  ctx.globalAlpha = 1;
}
// the wind lifting the fallen snow along the quay in thin streams (on dry nights: the city's own, clear, snow, leaves)
const DRIFTS = Array.from({ length: 46 }, (_, i) => ({ y: 209 + Math.floor(hash(i, 1, 81) * 58), n: 3 + Math.floor(hash(i, 2, 81) * 4), ph: hash(i, 3, 81), len: 6 + Math.floor(hash(i, 4, 81) * 12) }));
function drawDrifts(t) {
  for (let i = 0; i < DRIFTS.length; i++) {
    const d = DRIFTS[i], { cyc, u } = cycle(t, d.n, d.ph), y = d.y + Math.round(hash(i, cyc, 82) * 4);
    const x = Math.round(lerp(-30, W + 10, u)), a = Math.sin(Math.PI * u) * 0.28;
    if (y < QY(x) + 3 || (x < CAFE.far && y < CAFE.base + 4)) continue;
    ctx.globalAlpha = a; R(x, y, d.len, 1, '#dfe6fa'); R(x + 3, y - 1, Math.max(2, d.len - 6), 1, '#c8d0ec');
  }
  ctx.globalAlpha = 1;
}
const WEATHER = weatherPlans(['snow']);
const WX_LAND = [[90, 160], [158, 204], [H + 4, H + 4]];
const toWx = list => list.map(l => ({ x: l.x, y: l.y, rx: l.r, ry: l.r, col: l.col }));
const WX = { land: WX_LAND, lights: toWx(SNOW_LIGHTS), leaves: { wind: [0, 20, W, 190] } };
const WX_BB = { ...WX, lights: toWx(SNOW_LIGHTS_BB) };

function drawTram(t) {
  const tr = TRAMS.find(T => t >= T.a && t < T.b);
  if (!tr) return;
  const u = (t - tr.a) / (tr.b - tr.a), x = Math.round(tr.dir > 0 ? lerp(92, 300, u) : lerp(300, 92, u)), y = SQ - 6;
  ctx.globalAlpha = smooth(0, 0.06, u) * (1 - smooth(0.94, 1, u));
  R(x, y, 20, 5, '#3a8a54'); R(x, y, 20, 1, '#8ac89a'); R(x, y + 3, 20, 1, '#e8eee8');
  for (let i = 2; i < 18; i += 3) R(x + i, y + 1, 2, 2, '#ffe0a0');
  R(x + 1, y + 5, 18, 1, '#1a1a26');
  ctx.globalAlpha = 1;
  halo(HL.tram, tr.dir > 0 ? x + 20 : x, y + 2, 0.6);
}
function renderWater() {
  return ditherFill(W, H - WL, ['#060a22', '#0a1030', '#0e163a', '#141c44', '#1c2450'], (i, y) => {
    let v = 0.5 - y / 60 + Math.max(0, 1 - Math.hypot((i - CATH.cx) / 80, y / 30)) * 0.3 + Math.max(0, 1 - Math.hypot((i - USP.cx) / 70, y / 26)) * 0.2;
    return clamp(v, 0, 1);
  });
}
function renderVignette() {
  const p = painter(W, H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const d = Math.hypot((x - W / 2) / (W * 0.62), (y - H * 0.45) / (H * 0.7));
    if (d > 0.84 && (d - 0.84) * 3 > bayer(x, y)) p.put(x, y, '#05060e', 90);
  }
  return p.done();
}
// the harbour's mirror: everything above the waterline, squashed and rippled, then streaks under the lights
function drawWater(t) {
  ctx.drawImage(WATER_C, 0, WL);
  const top = WL, rows = 44, sq = 1.6;
  for (let k = 0; k < rows; k++) {
    const sy = top - 1 - Math.floor(k * sq);
    const off = Math.round(Math.sin(TAU * 60 * t / LOOP + k * 0.55) * (0.6 + k * 0.05) + Math.sin(TAU * 240 * t / LOOP - k * 1.3) * 0.8);
    ctx.globalAlpha = 0.34 * (1 - k / (rows + 16));
    ctx.drawImage(MIRROR_SRC, 0, sy, W, 1, off, top + k, W, 1);
  }
  ctx.globalAlpha = 1;
  for (const [sx, w, col, a] of STREAKS) streak(ctx, t, sx, w, col, a, sx * 0.37, WL + 1, 205);
}
const STREAKS = [[CATH.cx, 20, '#eef0fa', 0.2], [CATH.cx - 40, 6, '#e8ecf8', 0.12], [CATH.cx + 40, 6, '#e8ecf8', 0.12],
  [USP.cx, 14, '#f0a070', 0.2], [WHEEL.cx, 10, '#a8c8ff', 0.18], [312, 8, '#5ad8c8', 0.2], [338, 4, '#ffd8a0', 0.16],
  [112, 3, '#ffd090', 0.2], [158, 3, '#ffd090', 0.18], [204, 3, '#ffd090', 0.18], [250, 3, '#ffd090', 0.18], [288, 3, '#ffd090', 0.18],
  [356, 3, '#ffc080', 0.16], [404, 3, '#ffc080', 0.16], [76, 3, '#ffc070', 0.16], [466, 3, '#ffd090', 0.14]];

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISP_C, BOAT_C, FAR_C, CATH_C, USP_C, FRONT_C, ALLAS_C, WBASE_C, WHEEL_C, QUAY_C, CAFE_C, BENCH_C, BB_AD, PITCH,
  WATER_C, VIG_C, MIRROR_SRC;
function prerender() {
  WINS.length = 0; FAR_WINS.length = 0;
  HL = {
    moon: makeHalo(40, '#9ab0ff', 0.2, 1.8), moonCore: makeHalo(14, '#fff2d0', 0.3, 1.5),
    cath: makeHalo(84, '#dfe6ff', 0.12, 1.4), cathCore: makeHalo(40, '#ffffff', 0.12, 1.5), usp: makeHalo(60, '#ff9a60', 0.12, 1.4),
    lamp: makeHalo(30, '#ffc878', 0.24, 1.6), lampCore: makeHalo(8, '#fff0c8', 0.55, 1.4), cafe: makeHalo(56, '#ffb060', 0.26, 1.5),
    stall: makeHalo(30, '#ffb060', 0.24, 1.5), nearLamp: makeHalo(44, '#ffc878', 0.22, 1.6), flame: makeHalo(14, '#ff9a40', 0.32, 1.5), bulb: makeHalo(4, '#ffc860', 0.45, 1.2), market: makeHalo(10, '#ffd090', 0.3, 1.5),
    wheel: makeHalo(36, '#8ab0ff', 0.12, 1.4), tram: makeHalo(5, '#ffe0a0', 0.5, 1.3), pool: makeHalo(16, '#5ad8c8', 0.25, 1.5),
    bbGlow: makeHalo(30, '#ff8a70', 0.14, 1.5), nav: makeHalo(3, '#4aff8a', 0.5, 1.2), ferry: makeHalo(18, '#ffd890', 0.2, 1.4),
    breath: makeHalo(3, '#e8ecff', 0.5, 1.2), bell: makeHalo(30, '#ffd890', 0.24, 1.5), belfry: makeHalo(10, '#ffc870', 0.4, 1.4), steam: [0, 1, 2, 3, 4, 5, 6].map(r => makeHalo(r + 1, '#d8e4f4', 0.55, 0.7)),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp); FAR_C = renderFar(); CATH_C = renderCathedral(); USP_C = renderUspenski();
  FRONT_C = renderWaterfront(); ALLAS_C = renderAllas(); WBASE_C = renderWheelBase(); WHEEL_C = renderWheelFrames();
  QUAY_C = renderQuay(); BOAT_C = renderBoat(); CAFE_C = renderCafe(); BENCH_C = renderBench(); BB_AD = renderBillboardAd();
  PITCH = makePitch(BB.w, BB.h, { accent: '#0a8f6a' });
  WATER_C = renderWater(); VIG_C = renderVignette();
  // the mirror's source: the static frame above the waterline (sky, roofs, both cathedrals, the waterfront, the wheel's frame)
  const [m, mx] = makeCanvas(W, H);
  for (const c of [SKY_C, FAR_C, CATH_C, USP_C, FRONT_C, WBASE_C, ALLAS_C]) mx.drawImage(c, 0, 0);
  MIRROR_SRC = m;
}

/* ================= Frame compositor (back to front) ================= */
const MARKET_LAMPS = [112, 136, 158, 182, 204, 228, 250, 272, 290];
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd = CROWD } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? SNOW_LIGHTS_BB : SNOW_LIGHTS;
  const show = env(t, SHOW.a, SHOW.b, 3);
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#e8eeff';
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.4 + 0.6 * wave(t, s.k, s.ph));
    ctx.fillRect(s.x, s.y, 1, 1);
    if (s.big) { ctx.globalAlpha *= 0.4; ctx.fillRect(s.x - 1, s.y, 3, 1); ctx.fillRect(s.x, s.y - 1, 1, 3); }
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  for (const L of WISP_C) { const off = Math.floor(frac(L.k * t / LOOP) * W); ctx.drawImage(L.c, off - W, L.y); ctx.drawImage(L.c, off, L.y); }
  drawGulls(t);
  ctx.drawImage(FAR_C, 0, 0);
  ctx.globalAlpha = 0.6; drawWins(t, FAR_WINS); ctx.globalAlpha = 1;
  if (wx.snow) drawSnow(0, t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // the two cathedrals in their floodlights
  halo(HL.cath, CATH.cx, 112, 0.9); halo(HL.cathCore, CATH.cx, 96, 0.8);
  ctx.drawImage(CATH_C, 0, 0);
  halo(HL.usp, USP.cx, 112, 0.85);
  ctx.drawImage(USP_C, 0, 0);
  const bg = env(t, BELL_GLOW.a, BELL_GLOW.b, 3), ring = bellRing(t);
  if (bg > 0) { ctx.globalAlpha = 0.6 * bg; R(USP.cx - 29, 96, 1, 7, '#ffd890'); R(USP.cx - 25, 96, 1, 7, '#ffd890'); ctx.globalAlpha = 1; halo(HL.belfry, USP.cx - 27, 99, bg); }
  if (ring > 0) halo(HL.bell, USP.cx - 27, 99, ring * 0.8);
  // the waterfront, the market's lamps, a tram, the SkyWheel, Allas steaming
  ctx.drawImage(FRONT_C, 0, 0);
  drawWins(t);
  for (const lx of MARKET_LAMPS) { R(lx, SQ - 5, 1, 1, '#fff0c8'); halo(HL.market, lx, SQ - 5, 0.8); }
  drawTram(t);
  ctx.drawImage(WBASE_C, 0, 0);
  drawWheel(t, show > 0.5);
  ctx.drawImage(ALLAS_C, 0, 0);
  halo(HL.pool, 312, ALLAS.y + 1, 0.8); halo(HL.pool, 336, ALLAS.y + 1, 0.6);
  drawAllas(t, crowd);
  drawSteam(t);
  // the harbour
  drawWater(t);
  drawFerry(t);
  drawSeal(t);
  ctx.drawImage(BOAT_C, 0, 0);
  if (wx.snow) drawSnow(1, t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  // the near quay, the café pavilion and its terrace, the bench, the people
  ctx.drawImage(QUAY_C, 0, 0);
  const dry = !wx.shared || wx.shared === 'snow' || wx.shared === 'leaves';
  if (dry) { drawGlints(t); drawDrifts(t); }
  ctx.drawImage(BENCH_C, 0, 0);
  ctx.drawImage(CAFE_C, 0, 0);
  drawCafeInside(t, crowd);
  drawCat(t);
  for (let j = 0; j < 12; j++) {                                    // wood smoke from the café's chimney, drifting east
    const { u } = cycle(t, 16, j / 12), r = Math.min(6, 1 + Math.floor(u * 7));
    halo(HL.steam[r], 49 + u * 34 + Math.sin(TAU * (u * 1.4 + 0.2)) * 3, 48 - u * 34, 0.55 * (1 - u) * smooth(0, 0.08, u));
  }
  halo(HL.cafe, 40, 206, 0.9); halo(HL.stall, 119, 181, 0.85);
  if (billboard) drawBillboard(t, pitch);
  drawCast(t, crowd);
  for (const [k, c] of CANDLES.entries()) {
    const f = hash(k, step(t, 1200), 41), g = hash(k, step(t, 1200) >> 1, 42);
    if (!c.small) { R(c.x - 1, c.y - 2, 3, 3, '#3a3438'); R(c.x - 1, c.y - 2, 3, 1, '#6a6068'); }
    R(c.x, c.y - 3 - (g < 0.4 ? 1 : 0), 1, 1, f < 0.5 ? '#fff0a0' : '#ffc050'); R(c.x, c.y - 2 - (c.small ? 0 : 1), 1, 1, '#ff8a2a');
    halo(HL.flame, c.x, c.y - 3, 0.7 + 0.3 * f);
  }
  for (const b of TREE_BULBS) { const on = 0.6 + 0.4 * wave(t, 20 + (b.k % 5) * 4, b.k * 0.41); R(b.x, b.y, 1, 1, on > 0.8 ? '#fff2c0' : b.k & 1 ? '#ff8a6a' : '#ffc860'); halo(HL.bulb, b.x, b.y, on * 0.6); }
  for (const b of STALL_BULBS) { const on = 0.7 + 0.3 * wave(t, 30 + (b.k % 3) * 6, b.k * 0.29); R(b.x, b.y, 1, 1, on > 0.85 ? '#fff2c0' : '#ffc860'); halo(HL.bulb, b.x, b.y, on * 0.6); }
  for (const b of STRING) { const on = 0.65 + 0.35 * wave(t, 24 + (b.k % 4) * 6, b.k * 0.37); R(b.x, b.y, 1, 1, on > 0.85 ? '#fff2c0' : '#ffc860'); halo(HL.bulb, b.x, b.y, on * 0.7); }
  for (const L of LAMPS) { halo(HL.lamp, L.x, L.top + 5, 0.8); halo(HL.lampCore, L.x, L.top + 4, 0.8); }
  halo(HL.nearLamp, NEAR_LAMP.x + 1, NEAR_LAMP.top + 7, 0.85); halo(HL.lampCore, NEAR_LAMP.x + 1, NEAR_LAMP.top + 6, 0.9);
  if (wx.snow) drawSnow(2, t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

const CROWD = 0.5;
export default {
  id: 'helsinki',
  name: 'Helsinki',
  tagline: 'Snow over the South Harbour',
  timeZone: 'Europe/Helsinki',
  accent: '#0a8f6a',
  focusX: CATH.cx,
  landmark: [CATH.cx - CATH.hw, CATH.cx + CATH.hw],     // the Cathedral, across its side porticoes
  landmarkTop: CATH.tip,                                // the Cathedral's cross
  billboard: BB,
  crowd: CROWD,
  spots: [                                              // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: CATH.cx - 28, y: CATH.tip, w: 57, h: 94 },                 // the Cathedral's domes, drum and portico
    { kind: 'bell', x: USP.cx - 32, y: USP.tip, w: 64, h: USP.base - USP.tip },     // Uspenski and its bell tower
    { kind: 'window', x: CAFE_WINS[1][0], y: 160, w: CAFE_WINS[1][1] - CAFE_WINS[1][0] + 1, h: 46 },   // the café's window
    { kind: 'water', x: 180, y: 164, w: 110, h: 22 },                                   // the harbour
  ],
  ambience: { wind: 0.24, water: 0.35, traffic: 0.06, crowd: 0.08 },
  events: [
    { t: 20, type: 'train', gain: 0.2, x: 100 }, { t: 150, type: 'train', gain: 0.2, x: 300 },
    { t: 30, type: 'gull', x: 400 }, { t: 110, type: 'gull', x: 80 }, { t: 214, type: 'gull', x: 400 },
    { t: 41, type: 'splash', x: SEAL.x, gain: 0.25 }, { t: 165, type: 'splash', x: SEAL.x, gain: 0.25 },
    ...BELLS.map(t => ({ t, type: 'bell', x: USP.cx - 27, gain: 0.6 })),
    { t: FERRY.horn, type: 'horn', x: 290 }, { t: FERRY.horn + 1, type: 'boat', dur: 52, pan: [0.2, 1] },
  ],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
